/**
 * API client.
 *
 * Three rules the server enforces, encoded here once so no caller has to
 * remember them:
 *
 *   1. `credentials: 'include'` on every request. app.technestack.com and
 *      api.technestack.com are different origins, so the session cookie is
 *      cross-site and is not sent otherwise. Locally the same is true of
 *      :5173 and :10000.
 *
 *   2. `X-Requested-With` on every non-GET. The session middleware returns
 *      403 missing_csrf_header without it, BEFORE parsing the body — so a
 *      missing header looks like a malformed request if you are debugging by
 *      status code alone. Any value passes; the header's presence is the
 *      signal, since a cross-site form post cannot set one.
 *
 *   3. `Origin` must equal the API's ALLOWED_ORIGIN. The browser sets it, so
 *      there is nothing to do here — but it means VITE_API_BASE_URL and the
 *      API's ALLOWED_ORIGIN are a matched pair. Change one, change the other.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

if (!BASE_URL) {
  // Failing loudly beats a stream of requests to the Vite dev server that
  // 404 with an HTML body and a confusing JSON parse error.
  throw new Error('VITE_API_BASE_URL is not set. See apps/web/.env.local.');
}

/**
 * A non-2xx response.
 *
 * `code` is the server's machine-readable `error` field — branch on that.
 * `message` is what to SHOW someone.
 *
 * This class used to discard the server's sentence entirely: it carried
 * `status` and `code` and called `super(code)`, so a screen could render only
 * `unbalanced`. The API goes to real trouble to produce "This entry is out of
 * balance by $12.50. Debits $100.00, credits $87.50" — §5 asks for exactly
 * that, and the services re-check rules the triggers already enforce for no
 * other reason than to say it in dollars. Throwing it away one layer above
 * where it was built defeated the requirement without failing anything.
 *
 * `message` falls back to `code` when the server sent no sentence, so nothing
 * is ever blank; some endpoints legitimately return a bare `error`.
 */
export class ApiError extends Error {
  /** The server's sentence, when it sent one. Absent is not an error. */
  readonly serverMessage?: string;
  /** The server's `detail`: a line number, a difference in cents, zod issues. */
  readonly detail?: unknown;
  /** Present on 429 only. */
  readonly retryAfterSeconds?: number;

  constructor(
    readonly status: number,
    readonly code: string,
    // An options object rather than three more positional parameters. Two
    // optional strings and an unknown in a row is how `detail` ends up being
    // passed as `retryAfterSeconds` by the next caller.
    options: { serverMessage?: string; detail?: unknown; retryAfterSeconds?: number } = {},
  ) {
    super(options.serverMessage ?? code);
    this.name = 'ApiError';
    this.serverMessage = options.serverMessage;
    this.detail = options.detail;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }

  /** No session, or it expired. The shell redirects rather than each caller. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';

  const headers = new Headers(init.headers);
  if (method !== 'GET') {
    headers.set('X-Requested-With', 'fetch');
    if (init.body !== undefined) headers.set('Content-Type', 'application/json');
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      method,
      headers,
      credentials: 'include',
    });
  } catch {
    // fetch rejects for network failure AND for a blocked CORS preflight, and
    // reports both as the same opaque TypeError. Naming both is more useful
    // than "Failed to fetch".
    throw new ApiError(0, 'network_unreachable');
  }

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const failure = body as {
      error?: string;
      message?: string;
      detail?: unknown;
      retryAfterSeconds?: number;
    } | null;

    throw new ApiError(response.status, failure?.error ?? 'unexpected_error', {
      serverMessage: failure?.message,
      detail: failure?.detail,
      retryAfterSeconds: failure?.retryAfterSeconds,
    });
  }

  return body as T;
}

/**
 * `?a=1&b=2`, or '' when every value is absent.
 *
 * Shared by the two receivables reads because they take the same three
 * parameters. Values are skipped when undefined or empty rather than sent
 * blank: the server's schemas are `.strict()` with defaults, and an empty
 * `counterparty` would filter on `%%` instead of not filtering.
 */
function searchSuffix(query: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const suffix = params.toString();
  return suffix ? `?${suffix}` : '';
}

// ------------------------------------------------------------------ auth

/** A bank connection, as Settings lists it: never its token (issue #50, stage D). */
export interface BankConnectionSummary {
  id: string;
  /** Mercury, or Plaid (migration 0076). */
  provider: 'mercury' | 'plaid';
  displayName: string;
  environment: 'sandbox' | 'production';
  connectedAt: string;
  disabled: boolean;
  syncStartDate: string;
  mappedAccounts: number;
  /** A live Plaid connection whose bank asks to be re-authorised. */
  reconnectNeeded: boolean;
}

/** A Plaid account and the chart account it feeds, with the day its import starts (0076). */
export interface PlaidAccountView {
  plaidAccountId: string;
  name: string;
  mask: string | null;
  mappedTo: { accountId: string; code: string; name: string; importFrom: string } | null;
  /**
   * The bank's balance (0077), null until a sync has read one. `currentCents`
   * is in Plaid's sign (a card's is what is owed); `comparison`, for a mapped
   * account, has the bank's figure in the feed's sign beside the feed balance
   * through today, and a difference only in the books' currency.
   */
  balance: {
    currentCents: number;
    availableCents: number | null;
    currency: string;
    asOf: string | null;
    comparison: {
      through: string;
      bankCents: number;
      feedCents: number;
      uncategorized: number;
      differenceCents: number | null;
    } | null;
  } | null;
}

export interface PlaidMapping {
  accounts: PlaidAccountView[];
  /** The business's bank accounts, each with the day an import onto it would start by default. */
  chart: Array<{ accountId: string; code: string; name: string; importFrom: string }>;
}

/** How a CSV's columns are read (plan §G.5). Columns count from 0. */
export interface CsvMapping {
  hasHeaderRow: boolean;
  dateColumn: number;
  dateFormat: 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD/MM/YYYY';
  descriptionColumn: number;
  amount: { kind: 'signed'; column: number } | { kind: 'split'; outColumn: number; inColumn: number };
  referenceColumn?: number | null;
}

/** A bank file as sent: its text, read in the browser, and its name. */
export interface BankFileInput {
  accountId: string;
  fileName: string;
  text: string;
  csvMapping?: CsvMapping | null;
}

export interface BankImportTransaction {
  date: string;
  /** Signed from the account's side: money in positive. */
  amountCents: number;
  ref: string;
  counterparty: string | null;
  memo: string | null;
}

/** What importing a file would do. Nothing is written by asking. */
export interface BankImportPreview {
  format: 'ofx' | 'qfx' | 'csv';
  accountRef: string;
  transactionCount: number;
  firstDate: string | null;
  lastDate: string | null;
  alreadyHeld: number;
  insideClosedBooks: number;
  moneyInCents: number;
  moneyOutCents: number;
  sample: BankImportTransaction[];
  warnings: Array<{ code: 'other_format' | 'other_account_ref' | 'mercury_account'; message: string }>;
}

export interface BankImportResult {
  importId: string;
  format: 'ofx' | 'qfx' | 'csv';
  rowsRead: number;
  rowsCreated: number;
  rowsSkipped: number;
}

export interface BankImportSummary {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  format: 'ofx' | 'qfx' | 'csv';
  fileName: string;
  rowsRead: number;
  rowsCreated: number;
  rowsSkipped: number;
  importedAt: string;
  importedBy: string;
}

/** What a member invitation is for (plan §F.5.6): shown before anything is asked. */
export interface JoinKey {
  email: string;
  businessName: string;
  role: Role;
  /** The address signs in already and proves its own password. */
  existingLogin: boolean;
}

export interface BegunJoin {
  existingLogin: boolean;
  otpauthUri: string | null;
  secret: string | null;
  token: string;
}

/** An open invitation into this business; never its code. */
export interface MemberInvite {
  id: string;
  email: string;
  role: Role;
  createdAt: string;
  expiresAt: string;
  createdByEmail: string | null;
}

/** A person in this business, as Settings' People lists them (plan §F.5.5). */
export interface Person {
  userId: string;
  email: string;
  role: Role;
  capabilities: Capability[];
  /** Grants (true) and withdrawals (false) on top of the role. */
  adjustments: Array<{ capability: Capability; allowed: boolean }>;
  joinedAt: string;
  lastSignInAt: string | null;
  removedAt: string | null;
  /** Their sign-in was deleted when they left their last business (0071). */
  signInErased: boolean;
}

/** A connection's Mercury accounts beside the chart's bank accounts. */
export interface BankMapping {
  mercury: Array<{
    mercuryAccountId: string;
    name: string;
    kind: string | null;
    mappedTo: { accountId: string; code: string; name: string } | null;
  }>;
  chart: Array<{ accountId: string; code: string; name: string; mappedHere: boolean }>;
}

/** Another address's sign-in linked to this one (issue #50, stage C). */
export interface LinkedLogin {
  email: string;
  linkedAt: string | null;
}

/** A business this login may switch to (issue #50). */
export interface Business {
  tenantId: string;
  name: string;
}

export interface Session {
  userId: string;
  tenantId: string;
  email: string;
  /** The business the session is in. */
  businessName: string | null;
  /** Every business this login signs in to, the current one included. */
  businesses: Business[];
  /**
   * The Privacy Policy's current version (its effective date) and whether this
   * sign-in has accepted it (migration 0065). Optional only for the tests'
   * hand-made sessions; the API always sends it.
   */
  privacy?: { version: string; accepted: boolean };
  /**
   * The user's role in this business, and what they may do here (migration
   * 0069), so a screen can leave out what the user may not use. The API is
   * the protection; this is only what the screen offers. Absent only on the
   * tests' hand-made sessions, which `can` treats as an admin's.
   */
  role?: Role;
  capabilities?: Capability[];
  /**
   * What kind of business this is (migration 0078), or null when it was never
   * recorded. Absent only on the tests' hand-made sessions, which a screen
   * treats as an S corporation, as it treats them as an admin.
   */
  business?: BusinessType | null;
  /**
   * Whether every line names a fund (migration 0079): true for every
   * non-profit. The editor's Fund column and Settings' Funds follow it.
   * Absent only on the tests' hand-made sessions, which keep no funds.
   */
  usesFunds?: boolean;
  /**
   * Whether the business keeps goods (migration 0086): an account holds stock.
   * Items in the nav, Stock On Hand on Reports and the goods entries follow
   * it. Absent only on the tests' hand-made sessions, which keep none.
   */
  keepsGoods?: boolean;
}

/** Mirrors `apps/api/src/services/projects.ts` (migration 0081). */
export type ProjectFunction = 'program_services' | 'management_and_general' | 'fundraising' | 'membership_development';

export interface Project {
  id: string;
  code: string | null;
  name: string;
  parentId: string | null;
  /** A non-profit's program's function; null for any other business's project. */
  function: ProjectFunction | null;
  isDefault: boolean;
  archivedAt: string | null;
  /** Its function is fixed once a posted line names it. */
  hasPostedLines: boolean;
  /** 0091: "Subdivision › Lot 12 › Unit B", and its level, 1 to 3. Absent on the tests' hand-made projects. */
  path?: string;
  depth?: number;
}

/** Mirrors `apps/api/src/services/phases.ts` (migration 0091): the second tag, one list the business shares. */
export interface Phase {
  id: string;
  code: string | null;
  name: string;
  parentId: string | null;
  archivedAt: string | null;
  hasLines: boolean;
  /** "Site Work › Excavation", and its level, 1 to 3. */
  path?: string;
  depth?: number;
}

export interface PhaseInput {
  code: string | null;
  name: string;
  parentId: string | null;
}

export interface ProjectInput {
  code: string | null;
  name: string;
  parentId: string | null;
  function: ProjectFunction | null;
}

/** Mirrors `apps/api/src/services/funds.ts` (migration 0079). */
export type FundRestriction = 'none' | 'purpose' | 'time' | 'perpetual';

export interface Fund {
  id: string;
  code: string;
  name: string;
  restriction: FundRestriction;
  boardDesignated: boolean;
  purpose: string | null;
  /** The date a time restriction lifts; only with `time`. */
  releaseOn: string | null;
  isDefault: boolean;
  archivedAt: string | null;
  /** Its restriction is fixed once a posted line names it. */
  hasPostedLines: boolean;
}

export interface FundInput {
  code: string;
  name: string;
  restriction: FundRestriction;
  boardDesignated: boolean;
  purpose: string | null;
  releaseOn: string | null;
}

/** Mirrors `apps/api/src/business-type.ts`: the statuses built today (0094; `lib/business-types.ts` has the rest). */
export interface BusinessType {
  legalForm:
    | 'sole_proprietorship'
    | 'single_member_llc'
    | 'multi_member_llc'
    | 'partnership'
    | 'corporation'
    | 'nonprofit_corporation'
    | 'trust'
    | 'llc';
  taxStatus: 'disregarded_entity' | 'partnership' | 's_corporation' | 'c_corporation' | 'tax_exempt';
  exemptSection: '501c3' | null;
  foundationStatus: 'public_charity' | 'private_nonoperating_foundation' | 'private_operating_foundation' | null;
  annualReturn: '990_n' | '990_ez' | '990' | '990_pf' | null;
}

/** A business type as signup states it: the API sets the rest (a private foundation files 990-PF). */
export interface BusinessTypeChoice {
  taxStatus: BusinessType['taxStatus'];
  legalForm: Exclude<BusinessType['legalForm'], 'llc'>;
  foundationStatus?: 'public_charity' | 'private_nonoperating_foundation' | 'private_operating_foundation';
  annualReturn?: '990_n' | '990_ez' | '990';
}

/** Mirrors `apps/api/src/services/partners.ts` (0096). */
export interface PartnerAccount {
  accountId: string;
  code: string;
  name: string;
}

export interface Partner {
  id: string;
  name: string;
  /** The party a guaranteed payment's entry names to be this partner's (0097, round 193's TW-2). */
  partyId: string | null;
  partyName: string | null;
  capital: PartnerAccount;
  contributions: PartnerAccount;
  draws: PartnerAccount;
}

/** One set of shares from a date, in millionths: 1,000,000 is 100%. */
export interface PartnerShareSet {
  effectiveOn: string;
  shares: Array<{ partnerId: string; profitMillionths: number; lossMillionths: number }>;
}

export interface PartnersList {
  partners: Partner[];
  /** Every set, newest first. */
  shareSets: PartnerShareSet[];
}

/** Mirrors `apps/api/src/services/partners-capital.ts` (0096). */
export interface PartnersCapitalStatement {
  fiscalYear: number;
  from: string;
  to: string;
  yearComplete: boolean;
  allocation: 'closed' | 'in_force' | 'unallocated';
  changedOn: string | null;
  partners: Array<{
    partnerId: string;
    name: string;
    startCents: number;
    contributionsCents: number;
    shareCents: number | null;
    drawsCents: number;
    /** Whatever else moved the partner's accounts: the end less everything named (round 193's TW-1). */
    otherChangesCents: number;
    endCents: number;
    guaranteedPaymentsCents: number;
  }>;
  unassignedGuaranteedPaymentsCents: number;
  earlierOpenYears: number[];
  notYetAllocatedCents: number;
  totalEquityCents: number;
}

/** Mirrors `apps/api/src/services/owners-equity.ts` (0094). */
export interface OwnersEquityStatement {
  fiscalYear: number;
  from: string;
  to: string;
  yearComplete: boolean;
  startCents: number;
  contributionsCents: number;
  netIncomeCents: number;
  /** Positive is money the owner took out; it is subtracted. */
  drawsCents: number;
  otherChangesCents: number;
  endCents: number;
}

/** Plan §F.5.2. Mirrors `apps/api/src/auth/capabilities.ts`. */
export type Capability =
  | 'edit_entries'
  | 'reconcile'
  | 'close_periods'
  | 'view_payroll'
  | 'run_payroll'
  | 'view_tax_ids'
  | 'manage_banks'
  | 'manage_business'
  | 'export_data'
  // 0084 (§B.17): a non-profit's giving report and year-end letters.
  | 'view_donors'
  | 'manage_users';

export type Role = 'admin' | 'bookkeeper' | 'viewer';


// ---------------------------------------------------------------- the archive

/**
 * One request to package everything this business holds.
 *
 * `stale` is a `running` row nothing will ever finish -- the process that was
 * building it is gone. The screen must not show it as in progress, because a
 * "Preparing…" that never resolves is how somebody concludes the feature is
 * broken. `downloadable` is the server's answer rather than the screen's
 * inference from `status`: an archive can be succeeded and have had its file
 * pruned, and only the server knows.
 */
export interface TenantExport {
  id: string;
  requestedAt: string;
  requestedBy: string;
  status: 'running' | 'succeeded' | 'failed' | 'expired';
  finishedAt: string | null;
  sizeBytes: number | null;
  rowCounts: Record<string, number> | null;
  errorMessage: string | null;
  stale: boolean;
  downloadable: boolean;
}


// ------------------------------------------------------------- your own login

/** What the Security section shows about this account's second factor. */
export interface SecurityState {
  totpEnrolledAt: string | null;
  /** Unused recovery codes. 0 means the next lost phone is a lockout. */
  remainingRecoveryCodes: number;
}

/** A candidate authenticator, not yet committed to the account. */
export interface BegunEnrolment {
  otpauthUri: string;
  secret: string;
  /** Carried back to confirm. Five minutes. */
  token: string;
}


/**
 * A candidate authenticator for a business that does not exist yet -- or, when
 * the invited address already signs in, no authenticator at all: the business
 * joins that login, proved with its own code (issue #50).
 */
export interface BegunSignup {
  existingLogin: boolean;
  otpauthUri: string | null;
  secret: string | null;
  token: string;
}

/** `status` is authoritative. A 200 from /auth/login is NOT a sign-in. */
export interface LoginResult {
  status: 'mfa_required' | 'authenticated';
  mfaRequired: boolean;
}

// -------------------------------------------------------------- bulk coding

/** §16.2 — one entry as a batch would code it. */
export interface BulkPlanEntry {
  headerId: string;
  entryDate: string;
  counterparty: string | null;
  /** The memo as it stands. The confirmation lets it be edited. */
  memo: string | null;
  hasReceipt: boolean;
  /** The receipts' filenames, oldest first. The confirmation names them. */
  receiptNames: string[];
  /**
   * §9.1 — the other Mercury view of this movement, or null. A DRAFT one can be
   * excluded in the same batch; see applyBulkCoding.
   */
  counterpart: {
    headerId: string;
    entryDate: string;
    counterparty: string | null;
    accountCode: string;
    accountName: string;
    status: string;
  } | null;
  /** The rule that produced it. §9.7 step 6 matters most where nobody looks. */
  rulePattern: string;
  lines: {
    accountId: string;
    accountCode: string;
    accountName: string;
    debitCents: number;
    creditCents: number;
  }[];
}

export type BulkSkipReason =
  | 'not_a_draft'
  | 'already_coded'
  | 'no_rule'
  | 'no_bank_amount'
  | 'no_mapped_account'
  | 'not_found';

export interface BulkSkipped {
  headerId: string;
  counterparty: string | null;
  reason: BulkSkipReason;
}

export interface BulkPlan {
  entries: BulkPlanEntry[];
  skipped: BulkSkipped[];
}

export interface BulkResult {
  applied: {
    headerId: string;
    /** `excluded`: the other side of a transfer, excluded because the operator asked. */
    status: 'coded' | 'posted' | 'failed' | 'excluded';
    message?: string;
  }[];
  skipped: BulkSkipped[];
}

// ---------------------------------------------------------------- transfers

/**
 * §9.1 — the other Mercury view of one movement between our own accounts.
 *
 * Inferred from a coincidence of date, amount and account, so it is shown and
 * never applied on its own. `status` decides what the screen says: a draft
 * counterpart means code one and exclude the other, a POSTED one means this
 * header is the duplicate.
 */
export interface Counterpart {
  headerId: string;
  entryDate: string;
  counterparty: string | null;
  bankAmountCents: number;
  status: JournalStatus;
  /** The ledger account THAT header's money moved on. */
  accountCode: string;
  accountName: string;
}

// ------------------------------------------------------------- coding rules

export type MatchType = 'contains' | 'regex';

export interface CodingRule {
  id: string;
  matchType: MatchType;
  pattern: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  /** Lower wins. 1 beats the default 100. */
  priority: number;
  isActive: boolean;
  createdAt: string;
  /** Headers whose counterparty this matches TODAY. Computed, never stored. */
  matchCount?: number;
  /** 0081: the fund and project (a non-profit's program) the rule names, or null (§C.6). Absent on the tests' hand-made rules. */
  fundId?: string | null;
  fundCode?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  /** Whether that fund or project has been archived since: its entries are refused until the rule names one in use (review PR-3). */
  fundArchived?: boolean;
  projectArchived?: boolean;
  /** 0091: the phase the categorized line goes in, or null. */
  phaseId?: string | null;
  phaseName?: string | null;
  phaseArchived?: boolean;
}

/** §9.7 step 1's answer, with step 6's provenance attached to it. */
export interface RuleSuggestion {
  accountId: string;
  accountCode: string;
  accountName: string;
  /** `rule: "AWS" → 6100 Software`. Shown verbatim; never paraphrased. */
  provenance: string;
  ruleId: string;
  pattern: string;
  matchType: MatchType;
  /** 0081: the rule's fund and project, which the editor puts on the categorized line. */
  fundId?: string | null;
  projectId?: string | null;
  /** 0091: the rule's phase, which the editor puts on the categorized line. */
  phaseId?: string | null;
}

export interface CreateCodingRuleBody {
  matchType: MatchType;
  pattern: string;
  accountId: string;
  priority?: number;
  fundId?: string | null;
  projectId?: string | null;
  phaseId?: string | null;
}

export interface UpdateCodingRuleBody {
  pattern?: string;
  accountId?: string;
  priority?: number;
  isActive?: boolean;
  /** Absent leaves it, null clears it. */
  fundId?: string | null;
  projectId?: string | null;
  phaseId?: string | null;
}

// ------------------------------------------------------------------ reports

/** One line of a cash-basis P&L. §6.4. */
export interface CashBasisLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  debitCents: number;
  creditCents: number;
  /** Always the CLEARING header's cash date, per §6.4 step 3. */
  cashDate: string;
  headerId: string;
  /** The invoice or bill this revenue came from, for a converted line. */
  sourceHeaderId: string | null;
  /** True for a line §6.4 step 3 emitted in place of a control line. */
  converted: boolean;
}

/**
 * §6.6's three numbers, and all three are needed to check the report.
 *
 * The invariant is `netIncomeCents === bankMovementCents - nonPnlMovementCents`.
 * A screen that shows only net income cannot tell the operator whether the
 * conversion closed, and a mutation that let bank lines leak into the P&L
 * passed every API test until both movement totals were asserted — the
 * identity still closed, because a stray bank line lands in the non-P&L bucket
 * and net income never moves.
 */
export interface CashBasisReport {
  from: string;
  to: string;
  lines: CashBasisLine[];
  /** `sum(credit - debit)` over the P&L lines. Positive is profit. */
  netIncomeCents: number;
  /** `sum(debit - credit)` over bank-role lines. Positive is cash in. */
  bankMovementCents: number;
  /** Draws, loan principal, fixed assets: real cash, not P&L. */
  nonPnlMovementCents: number;
}

/**
 * A file from the API, for a download. Not `request`, which parses JSON: this
 * reads the bytes. A refusal is JSON and is read the same way `request` reads
 * one, so the screen gets the server's sentence either way.
 */
async function fetchFile(path: string): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, { credentials: 'include' });
  } catch {
    throw new ApiError(0, 'network_unreachable');
  }
  if (!response.ok) {
    const failure = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
    throw new ApiError(response.status, failure?.error ?? 'unexpected_error', {
      serverMessage: failure?.message,
    });
  }
  return response.blob();
}

export const api = {
  me: () => request<Session>('/auth/me'),

  /** Records this sign-in's acceptance of the Privacy Policy version shown. */
  acceptPrivacy: (version: string) =>
    request<{ status: 'accepted'; version: string }>('/auth/privacy-acceptance', {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),

  login: (email: string, password: string) =>
    request<LoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  verifyTotp: (code: string) =>
    request<{ status: 'authenticated'; expiresAt: string }>('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /**
   * THE FIELD NAMES HERE ARE THE SERVER'S, AND ONE OF THEM WAS NOT.
   *
   * This declared `recoveryCodesRemaining`; the route sends
   * `remainingRecoveryCodes` (`routes/auth.ts`). The names differed, so the
   * value was `undefined` forever while TypeScript reported the call as fully
   * typed -- the same shape as a SELECT that drops a column, and on the one
   * number that says the ten codes are running out.
   *
   * Nothing renders it yet. It arriving is the precondition for the Settings
   * section that will.
   */
  verifyRecoveryCode: (code: string) =>
    request<{
      status: 'authenticated';
      remainingRecoveryCodes: number;
      warning: string;
    }>('/auth/mfa/recovery', { method: 'POST', body: JSON.stringify({ code }) }),

  logout: () => request<{ status: 'signed_out' }>('/auth/logout', { method: 'POST' }),

  /** Moves this session into another business the login signs in to. */
  switchBusiness: (tenantId: string) =>
    request<{ status: 'switched'; tenantId: string }>('/auth/switch', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    }),

  // ---------------------------------------------------------------- signup

  /**
   * Step one: the key alone.
   *
   * Answers with the address the invite is for AND NOTHING ELSE -- no slug, no
   * timezone list, no password policy. An unauthenticated page must not
   * describe the system's shape, which is why this is a step of its own.
   */
  signupKey: (code: string) =>
    request<{ email: string; existingLogin: boolean }>('/signup/key', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** Step two: the details, validated. Writes nothing. */
  signupBegin: (input: {
    code: string;
    name: string;
    timezone: string;
    fiscalYearStartMonth: number;
    password: string;
    businessType: BusinessTypeChoice;
  }) => request<BegunSignup>('/signup/begin', { method: 'POST', body: JSON.stringify(input) }),

  /** Step three: the code that creates everything, and signs them in. */
  signupComplete: (token: string, code: string) =>
    request<{ status: 'created'; accountsSeeded: number; codes: string[] }>(
      '/signup/complete',
      { method: 'POST', body: JSON.stringify({ token, code }) },
    ),


  // ----------------------------------------------------------- your own login

  readSecurity: () => request<SecurityState>('/security'),

  // ------------------------------------------------------- bank connections

  listBankConnections: () => request<{ connections: BankConnectionSummary[] }>('/bank-connections'),

  /** Whether Plaid is switched on here (0076). */
  plaidStatus: () => request<{ configured: boolean; environment: 'sandbox' | 'production' }>('/plaid/status'),
  plaidLinkToken: () => request<{ linkToken: string }>('/plaid/link-token', { method: 'POST' }),
  connectPlaid: (input: { publicToken: string; displayName: string; syncStartDate: string; code: string }) =>
    request<{ connectionId: string; accounts: PlaidAccountView[] }>('/plaid/connections', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  readPlaidMapping: (connectionId: string) =>
    request<PlaidMapping>(`/plaid/connections/${encodeURIComponent(connectionId)}/accounts`),
  setPlaidMapping: (connectionId: string, plaidAccountId: string, accountId: string | null, importFrom: string | null) =>
    request<{ status: 'mapped' }>(
      `/plaid/connections/${encodeURIComponent(connectionId)}/accounts/${encodeURIComponent(plaidAccountId)}`,
      { method: 'PUT', body: JSON.stringify({ accountId, importFrom }) },
    ),
  plaidUpdateLinkToken: (connectionId: string, code: string) =>
    request<{ linkToken: string }>(`/plaid/connections/${encodeURIComponent(connectionId)}/update-link-token`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /**
   * Connects a Mercury account: the token is tried against Mercury before it
   * is stored, and `code` is this sign-in's authenticator or recovery code.
   */
  connectMercury: (input: { displayName: string; token: string; syncStartDate: string; code: string }) =>
    request<{ connectionId: string }>('/bank-connections', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  readBankMapping: (connectionId: string) =>
    request<BankMapping>(`/bank-connections/${encodeURIComponent(connectionId)}/accounts`),

  setBankMapping: (connectionId: string, mercuryAccountId: string, accountId: string | null) =>
    request<{ status: 'mapped' }>(`/bank-connections/${encodeURIComponent(connectionId)}/mappings`, {
      method: 'PUT',
      body: JSON.stringify({ mercuryAccountId, accountId }),
    }),

  replaceBankToken: (connectionId: string, token: string, code: string) =>
    request<{ status: 'replaced'; missing: string[] }>(
      `/bank-connections/${encodeURIComponent(connectionId)}/token`,
      { method: 'POST', body: JSON.stringify({ token, code }) },
    ),

  /** Switches the connection off and deletes its stored token (migration 0064). */
  disconnectBank: (connectionId: string) =>
    request<{ status: 'disconnected' }>(
      `/bank-connections/${encodeURIComponent(connectionId)}/disconnect`,
      { method: 'POST' },
    ),

  /** Puts a new, proved token on a disconnected connection, which comes back on. */
  reconnectBank: (connectionId: string, token: string, code: string) =>
    request<{ status: 'reconnected'; missing: string[] }>(
      `/bank-connections/${encodeURIComponent(connectionId)}/reconnect`,
      { method: 'POST', body: JSON.stringify({ token, code }) },
    ),

  /** Everyone in this business, with what each may do. */
  listPeople: () => request<{ people: Person[] }>('/people'),

  setPersonRole: (userId: string, role: Role) =>
    request<{ status: 'saved' }>(`/people/${encodeURIComponent(userId)}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  /** Grants (true), withdraws (false) or returns to the role's default (null). */
  setPersonAccess: (userId: string, capability: Capability, allowed: boolean | null) =>
    request<{ status: 'saved' }>(
      `/people/${encodeURIComponent(userId)}/access/${encodeURIComponent(capability)}`,
      { method: 'PUT', body: JSON.stringify({ allowed }) },
    ),

  /** This business's open invitations. */
  listMemberInvites: () => request<{ invites: MemberInvite[] }>('/people/invites'),

  /** Invites a person. The code comes back ONCE, here. */
  inviteMember: (email: string, role: Role) =>
    request<{ id: string; email: string; code: string; expiresAt: string }>('/people/invites', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),

  revokeMemberInvite: (inviteId: string) =>
    request<{ status: 'revoked' }>(`/people/invites/${encodeURIComponent(inviteId)}/revoke`, { method: 'POST' }),

  /** Joining from an invitation: the code alone, then the password, then the authenticator. */
  joinKey: (code: string) => request<JoinKey>('/join/key', { method: 'POST', body: JSON.stringify({ code }) }),
  joinBegin: (code: string, password: string) =>
    request<BegunJoin>('/join/begin', { method: 'POST', body: JSON.stringify({ code, password }) }),
  joinComplete: (token: string, code: string) =>
    request<{ status: 'joined'; codes: string[] }>('/join/complete', {
      method: 'POST',
      body: JSON.stringify({ token, code }),
    }),

  removePerson: (userId: string) =>
    request<{ status: 'removed'; signInErased: boolean }>(`/people/${encodeURIComponent(userId)}/remove`, {
      method: 'POST',
    }),

  /** The business's imported bank files, newest first. */
  listBankImports: () => request<{ imports: BankImportSummary[] }>('/bank-imports'),

  /** What importing this file would do. Writes nothing. */
  previewBankImport: (input: BankFileInput) =>
    request<BankImportPreview>('/bank-imports/preview', { method: 'POST', body: JSON.stringify(input) }),

  /** Imports the file: each transaction not already held becomes a draft. */
  importBankFile: (input: BankFileInput) =>
    request<BankImportResult>('/bank-imports', { method: 'POST', body: JSON.stringify(input) }),

  /** The other addresses linked to this sign-in. */
  listLinkedLogins: () => request<{ linked: LinkedLogin[] }>('/security/links'),

  /**
   * Links another address's sign-in: its password and a current code from its
   * authenticator, and a code from this sign-in's own (or a recovery code).
   */
  linkLogin: (input: { email: string; password: string; theirCode: string; code: string }) =>
    request<{ linked: LinkedLogin[] }>('/security/links', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Takes this sign-in out of its link, with its own current code. */
  leaveLink: (code: string) =>
    request<{ linked: LinkedLogin[] }>('/security/links/leave', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /**
   * Ten fresh codes, replacing every old one, shown once and never again.
   *
   * `code` is a CURRENT second factor -- from the authenticator or one of the
   * existing recovery codes. The server decides which by its shape.
   */
  reissueRecoveryCodes: (code: string) =>
    request<{ codes: string[] }>('/security/recovery-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** Writes nothing. The account keeps its current authenticator until confirm. */
  beginTotpEnrolment: () =>
    request<BegunEnrolment>('/security/totp/begin', { method: 'POST' }),

  /**
   * Commits it, and returns the fresh recovery codes that come with it.
   *
   * TWO CODES, and both are load-bearing. `code` comes from the NEW device and
   * proves it works; `currentCode` comes from the authenticator being replaced
   * (or a recovery code) and proves this is the owner rather than a borrowed
   * session. Without the second, a live cookie alone replaced the second factor
   * and destroyed every recovery code.
   */
  confirmTotpEnrolment: (token: string, code: string, currentCode: string) =>
    request<{ codes: string[] }>('/security/totp/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, code, currentCode }),
    }),


  // -------------------------------------------------------------- the archive

  listExports: () => request<{ exports: TenantExport[] }>('/exports'),

  /** 202 and a `running` row. The file does not exist yet. */
  requestExport: () =>
    request<{ export: TenantExport }>('/exports', { method: 'POST' }),

  /**
   * A signed URL, not the bytes.
   *
   * The archive is tens or hundreds of megabytes; streaming it through the API
   * would hold the whole thing in a 512 MB container. The caller opens the URL,
   * which is the legitimate DOWNLOAD path -- the server signs it with
   * `Content-Disposition: attachment`, and `attachment-viewer.test.ts` draws
   * that line: `inline` must be read in the overlay, `attachment` is saved.
   */
  exportDownloadUrl: (id: string) =>
    request<{ url: string; filename: string }>(`/exports/${id}/download`),


  syncStatus: () => request<SyncStatus>('/sync/status'),

  /** Runs every live bank of this business now (issue #54). Rate limited by the server. */
  syncNow: () => request<{ banks: ManualSyncResult[] }>('/sync/mercury', { method: 'POST' }),

  /** §9.1 — what the sync found and would not decide, newest first. */
  listSyncExceptions: () => request<{ exceptions: SyncException[] }>('/sync/exceptions'),

  /** Mark one handled. It changes nothing in the books. */
  resolveSyncException: (id: string) =>
    request<{ id: string; resolved: boolean }>(`/sync/exceptions/${id}/resolve`, { method: 'POST' }),

  // ---------------------------------------------------------------- journals

  listJournals: (filters: JournalFilters = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.toString();
    return request<JournalList>(`/journals${suffix ? `?${suffix}` : ''}`);
  },

  getJournal: (id: string) => request<{ entry: JournalEntry }>(`/journals/${id}`),

  /**
   * §9.1. Note the path: `/journal/headers/:id/dismiss`, singular, not
   * `/journals/:id`. It lives in the sync routes because "dismissed drafts
   * stay dismissed" is a sync behaviour, and it predates the journal routes.
   * The inconsistency is real; it is not worth a breaking change to fix from
   * a screen, so it is spelled out here instead of quietly normalised.
   *
   * Takes no expectedVersion — it is conditional on the row still being a
   * draft and returns 409 `not_a_draft` otherwise.
   */
  dismissJournal: (id: string) =>
    request<{ id: string; status: 'dismissed' }>(`/journal/headers/${id}/dismiss`, {
      method: 'POST',
    }),

  undismissJournal: (id: string) =>
    request<{ id: string; status: 'draft' }>(`/journal/headers/${id}/undismiss`, {
      method: 'POST',
    }),

  /**
   * §17 — a header that did not come from the bank.
   *
   * The 2025 invoices are not Mercury transactions: no cash moved when they
   * were issued, so nothing syncs them. There is no `source`, no
   * `bankAmountCents` and no `mercuryTransactionId` in this body, and their
   * absence is the point -- "the bank told us this" is a claim only the bank
   * is entitled to make, and §9.3 measures coded lines against it.
   */
  createJournal: (body: CreateJournalBody) =>
    request<{ entry: { id: string; entryDate: string; version: number } }>('/journals', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ------------------------------------------------------------- clearings

  /**
   * §6.3 — the two halves of payment matching, read separately.
   *
   * Open invoices are debit-side A/R lines with `open_cents > 0`; unapplied
   * payments are credit-side lines with `unapplied_cents > 0`. Both come from
   * `control_line_balances` (migration 0016), which carries all four balance
   * columns on every row — so reading the wrong one raises nothing and returns
   * a number that looks entirely reasonable. The API keeps them apart for that
   * reason and so does this.
   */
  listOpenInvoices: (query: ReceivablesQuery & { invoicesOnly?: boolean } = {}) =>
    request<OpenInvoicesResult>(`/invoices/open${searchSuffix(query)}`),

  /** 0084 (§B.17.5): every party who gave in the year; `view_donors`, a non-profit's only. */
  givingReport: (year: number) => request<GivingReport>(`/donors/giving${searchSuffix({ year })}`),

  /** 0085 (§B.18.4): a private foundation's 990-PF worksheet for the fiscal year `asOf` falls in; 404 for any other business. */
  form990Pf: (asOf: string) => request<Pf990Worksheet>(`/reports/990-pf${searchSuffix({ asOf })}`),

  /** 0085 (§B.18.5): the CPA's markings for a fiscal year; changing them needs manage_business. */
  pfMarkings: (year: number) => request<PfMarkings>(`/foundation/markings${searchSuffix({ year })}`),

  savePfMarkings: (year: number, markings: PfMarking[]) =>
    request<PfMarkings>('/foundation/markings', { method: 'PUT', body: JSON.stringify({ year, markings }) }),

  /** Copies the year before's markings, only where this year has none for the account. */
  copyPfMarkings: (year: number) =>
    request<{ copied: number }>('/foundation/markings/copy', { method: 'POST', body: JSON.stringify({ year }) }),

  /** 0092 (§B.20): the Return Lines report for the fiscal year `asOf` falls in; 404 for a business that files no Form 990, or another's form. */
  returnLines: (form: ReturnLineForm, asOf: string) => request<ReturnLinesReport>(`/returns/lines${searchSuffix({ form, asOf })}`),

  /** Each account's line on a form, and the current catalogue's lines; setting one needs manage_business. */
  returnLineMap: (form: ReturnLineForm) => request<ReturnLineMap>(`/returns/line-map${searchSuffix({ form })}`),

  /** Puts an account on a line, or takes it off with `part` and `line` null. */
  setReturnLine: (form: ReturnLineForm, accountId: string, part: string | null, line: string | null) =>
    request<{ accountId: string; mapping: ReturnLineMapping | null }>('/returns/line-map', {
      method: 'PUT',
      body: JSON.stringify({ form, accountId, part, line }),
    }),

  /** Writes the suggested line for each account with none on the form; nothing else. */
  applySuggestedReturnLines: (form: ReturnLineForm) =>
    request<{ written: number }>('/returns/line-map/suggested', { method: 'POST', body: JSON.stringify({ form }) }),

  /** 0084 (§B.17.5): one donor's year-end letter; refused without the business's legal name and EIN. */
  donorLetter: (partyId: string, year: number) =>
    request<DonorLetter>(`/donors/${partyId}/letter${searchSuffix({ year })}`),

  /** §6.8 — every invoice, draft to paid, for the Invoices screen. */
  listInvoices: (query: InvoicesQuery = {}) =>
    request<InvoicesResult>(`/invoices${searchSuffix(query)}`),

  listUnappliedPayments: (query: ReceivablesQuery = {}) =>
    request<UnappliedPaymentsResult>(`/payments/unapplied${searchSuffix(query)}`),

  /**
   * The live matches applied from one payment line.
   *
   * This is what makes voiding reachable at all: `POST /clearings/:id/void`
   * has existed since Phase 2 and took an id nothing could tell a client.
   */
  listPaymentClearings: (paymentDetailId: string) =>
    request<{ clearings: PaymentClearing[] }>(`/payments/${paymentDetailId}/clearings`),

  /**
   * §9.3 — allocate one payment across one or more invoices, in a single
   * transaction. All of them or none: an operator splitting a deposit across
   * four invoices must not end up with two applied and two lost.
   */
  allocatePayment: (body: AllocatePaymentBody) =>
    request<{ allocation: AllocationResult }>('/clearings', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * §6.3 — a match is UNDONE, never deleted. The reason is required by the
   * server and is the audit trail; there is no way to remove the row.
   */
  voidClearing: (id: string, reason: string) =>
    request<void>(`/clearings/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * §6.5 — the shortfall is bad debt, not a bank fee.
   *
   * There is no `kind` parameter here and there must not be one. A bank fee is
   * an ordinary expense line the operator adds to the payment entry in the
   * editor BEFORE posting it, after which the payment clears the invoice in
   * full. A write-off is this call, which builds its own header with
   * `cash_date` null.
   *
   * The two are numerically identical and accounting-wise opposite, and §6.5
   * is explicit that the system cannot infer which applies — a parameter with
   * a default would be an inference. The operator classifies by choosing which
   * request to send, which is why the screen asks rather than offering a
   * checkbox.
   */
  writeOffShortfall: (body: WriteOffBody) =>
    request<{ writeOff: WriteOffResult }>('/writeoffs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // -------------------------------------------------------- party aliases

  /**
   * §7 — the party a counterparty name belongs to, or null.
   *
   * Matched EXACTLY on the normalised value, against party names first and
   * then aliases. `counterparty` holds Mercury's structured counterparty name
   * rather than the raw bank description, so every value is a clean name and a
   * substring rule would only cost precision.
   *
   * `{ party: null }` is the ordinary answer, not a failure: most of the 40
   * distinct counterparties in production match nothing.
   */
  resolveParty: (counterparty: string) =>
    request<{ party: ResolvedParty | null }>(
      `/parties/resolve?counterparty=${encodeURIComponent(counterparty)}`,
    ),

  listPartyAliases: (partyId: string) =>
    request<{ aliases: PartyAlias[] }>(`/parties/${partyId}/aliases`),

  /** Every alias this tenant has. One request rather than one per party. */
  listAllPartyAliases: () => request<{ aliases: PartyAlias[] }>('/party-aliases'),

  addPartyAlias: (partyId: string, alias: string) =>
    request<{ alias: PartyAlias }>(`/parties/${partyId}/aliases`, {
      method: 'POST',
      body: JSON.stringify({ alias }),
    }),

  removePartyAlias: (aliasId: string) =>
    request<void>(`/party-aliases/${aliasId}`, { method: 'DELETE' }),

  /**
   * Every distinct counterparty, commonest first, and the party it maps to.
   *
   * Renamed from `listUnfiledCounterparties`, which asked the wrong question:
   * it filtered on headers with no `party_id` and then returned rows that
   * plainly HAD a party, because they were there on account of unfiled entries
   * rather than an unmapped name.
   */
  listCounterparties: () =>
    request<{ counterparties: CounterpartySummary[] }>('/counterparties'),

  listAccounts: () => request<{ accounts: Account[] }>('/accounts'),

  createAccount: (body: CreateAccountBody) =>
    request<{ account: Account }>('/accounts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateAccount: (id: string, body: UpdateAccountBody) =>
    request<{ account: Account }>(`/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  listParties: (q?: string, includeInactive?: boolean) => {
    const query = new URLSearchParams();
    if (q) query.set('q', q);
    if (includeInactive) query.set('includeInactive', 'true');
    const suffix = query.toString();
    return request<{ parties: Party[] }>(`/parties${suffix ? `?${suffix}` : ''}`);
  },

  // ------------------------------------------------------------- receipts

  listAttachments: (headerId: string) =>
    request<{ attachments: Attachment[] }>(`/journals/${headerId}/attachments`),

  presignAttachment: (headerId: string, body: PresignBody) =>
    request<{ upload: PresignedUpload }>(`/journals/${headerId}/attachments/presign`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  recordAttachment: (headerId: string, body: PresignBody & { key: string }) =>
    request<{ attachment: Attachment }>(`/journals/${headerId}/attachments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * A short-lived signed URL for one receipt.
   *
   * `disposition` is not a detail. `attachment` makes the browser SAVE the
   * file, which is what window.open wants; `inline` makes it RENDER, which is
   * the only thing an <iframe> can do anything with. An iframe pointed at an
   * attachment URL starts a download and leaves an empty frame -- no error,
   * nothing in the console, and a screen that looks broken for a reason that
   * is not on the screen.
   *
   * Defaulted to `attachment` so the editor's existing download path is
   * unchanged and viewing has to be asked for.
   */
  attachmentUrl: (id: string, disposition: Disposition = 'attachment') =>
    request<{ url: string; filename: string; disposition: Disposition }>(
      `/attachments/${id}/url?disposition=${disposition}`,
    ),

  deleteAttachment: (id: string) =>
    request<void>(`/attachments/${id}`, { method: 'DELETE' }),

  updateParty: (id: string, body: UpdatePartyBody) =>
    request<{ party: Party }>(`/parties/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  createParty: (body: CreatePartyBody) =>
    request<{ party: Party }>('/parties', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * §9.3 — replace-all, not patch. The whole set of lines goes in one call
   * because that is what the editor does: the operator sees the entire entry
   * and saves it.
   *
   * `expectedVersion` must be the version that came back from the last read or
   * write of this entry. Never compute or increment it — a trigger owns the
   * increment (migration 0013) precisely so no writer can get it wrong, and
   * the response carries the new value to use next.
   */
  /**
   * §6.1 — record when an entry is due, and nothing else.
   *
   * Its own call rather than part of `saveJournalLines`, which needs the
   * entry's version and all of its lines. A due date is not part of the double
   * entry; see the service for why it carries no expected version.
   */
  /** §6.7 — bills with money still owed. The payables mirror of listOpenInvoices. */
  listOpenBills: (query: ReceivablesQuery = {}) =>
    request<OpenBillsResult>(`/bills/open${searchSuffix(query)}`),

  setDueDate: (id: string, dueDate: string | null) =>
    request<{ id: string; dueDate: string | null }>(`/journals/${id}/terms`, {
      method: 'PATCH',
      body: JSON.stringify({ dueDate }),
    }),

  saveJournalLines: (id: string, body: SaveLinesBody) =>
    request<{ entry: SavedDraft }>(`/journals/${id}/lines`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  postJournal: (id: string, expectedVersion: number) =>
    request<{ entry: PostedEntry }>(`/journals/${id}/post`, {
      method: 'POST',
      body: JSON.stringify({ expectedVersion }),
    }),

  /**
   * §5.5 as revised by migration 0027 — delete an entry entered by hand.
   *
   * The version travels on the query string, not in a body: some clients and
   * proxies drop the body of a DELETE, and a delete that silently lost its
   * version check would remove whatever the entry had become since the screen
   * loaded. A bank transaction answers 409 `not_deletable`.
   */
  deleteJournal: (id: string, expectedVersion: number) =>
    request<void>(`/journals/${id}${searchSuffix({ expectedVersion })}`, { method: 'DELETE' }),

  /**
   * §9.6 — reverse a posted entry: a new posted entry dated the tenant's today,
   * every debit and credit swapped. The reason is required and recorded.
   */
  reverseJournal: (id: string, reason: string) =>
    request<{ reversal: ReversalResult }>(`/journals/${id}/reverse`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * §5.7 — every recorded change to an entry, one element per save.
   *
   * The ORDER of `events` inside one change is not meaningful: they were
   * written by one transaction with one timestamp. A screen must not read a
   * sequence into it.
   */
  getJournalHistory: (id: string) =>
    request<{ changes: EntryChange[] }>(`/journals/${id}/history`),

  /**
   * §6.3 — the live payment matches on an entry's lines, from this entry's
   * side. The same list a save refused with `matched_line` carries in
   * `detail.matches`.
   */
  getJournalMatches: (id: string) =>
    request<{ matches: EntryMatch[] }>(`/journals/${id}/matches`),

  /**
   * §6.9 — the books' lock date, and what closing through `through` would
   * leave behind when it is given.
   */
  getBooksLock: (through?: string) =>
    request<BooksLock>(`/books/lock${searchSuffix({ through })}`),

  /**
   * §6.9 — close the books through a date, or reopen them with null. Null is
   * sent explicitly, never implied by an empty body. A date that has not ended
   * answers 422 `not_yet_ended`.
   */
  setBooksLock: (lockedThrough: string | null) =>
    request<BooksLock>('/books/lock', {
      method: 'PUT',
      body: JSON.stringify({ lockedThrough }),
    }),

  /** §6.9 — every close and reopen, newest first, with who did it. */
  getBooksLockHistory: () => request<{ changes: BooksLockChange[] }>('/books/lock/history'),

  // ------------------------------------------------------- the year-end close

  /** Migration 0052 — every fiscal year, with its close or what closing it would do. */
  listFiscalYears: () => request<FiscalYears>('/fiscal-years'),

  /** Write the closing entry for a year. 409 when it is closed, out of order or locked. */
  closeFiscalYear: (year: number) =>
    request<FiscalYearCloseResult>(`/fiscal-years/${year}/close`, { method: 'POST' }),

  /** Delete the closing entry, with a reason; recorded beside 0027's deletion record. */
  reopenFiscalYear: (year: number, reason: string) =>
    request<{ year: number; reopenedHeaderId: string }>(`/fiscal-years/${year}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ---------------------------------------------------------------- payroll

  /** §18.3 — every employee, with whether an SSN is on file. Never the SSN. */
  listEmployees: () => request<{ employees: EmployeeSummary[] }>('/payroll/employees'),

  /** One employee: the SSN's last four digits at most, and every W-4. */
  getEmployee: (id: string) => request<{ employee: Employee }>(`/payroll/employees/${id}`),

  createEmployee: (body: CreateEmployeeBody) =>
    request<{ employee: Employee }>('/payroll/employees', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Only the fields sent change. `ssn` replaces the number on file and null or
   * blank REMOVES it, so a details form must leave `ssn` out entirely.
   */
  updateEmployee: (id: string, body: UpdateEmployeeBody) =>
    request<{ employee: Employee }>(`/payroll/employees/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * An employee entered by mistake or to test, with their W-4s, pay rates and
   * opening totals, and refused once payroll has paid them. The database keeps
   * nothing of them; backups already taken still do.
   */
  deleteEmployee: (id: string) => request<void>(`/payroll/employees/${id}`, { method: 'DELETE' }),

  /** 0083 — the split of an employee's pay, replaced whole; an empty list goes back to the defaults. */
  setEmployeeAllocation: (id: string, shares: ShareInput[]) =>
    request<{ allocation: AllocationShare[] }>(`/payroll/employees/${id}/allocation`, {
      method: 'PUT',
      body: JSON.stringify({ shares }),
    }),

  /** §18.3 — one W-4 as it was filled in. It takes effect on its own date. */
  recordW4: (employeeId: string, body: RecordW4Body) =>
    request<{ w4: W4 }>(`/payroll/employees/${employeeId}/w4s`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** A W-4 entered by mistake. A W-4 is never edited, so removal is the correction. */
  removeW4: (id: string) => request<void>(`/payroll/w4s/${id}`, { method: 'DELETE' }),

  /** Issue #44 — the EIN, legal name and address the filled Form 941 and the W-2 worksheet carry. */
  getBusinessDetails: () => request<{ details: BusinessDetails; type: BusinessType | null }>('/business/details'),

  /** The legal form, within how the business is taxed (0078, 0094): it moves no number. */
  setLegalForm: (legalForm: Exclude<BusinessType['legalForm'], 'llc'>) =>
    request<{ type: BusinessType }>('/business/legal-form', { method: 'PUT', body: JSON.stringify({ legalForm }) }),

  /** A business's projects, a non-profit's programs (0081); an empty list for one that has none. */
  listProjects: () => request<{ projects: Project[] }>('/projects'),

  /** N7 (§B.11.1.5): the dry run of a QuickBooks bundle, and the digest Import must send back. Writes nothing. */
  quickbooksCheck: (bundle: unknown) =>
    request<{ digest: string; plan: ImportPlan }>('/quickbooks/check', { method: 'POST', body: JSON.stringify({ bundle }) }),
  /** Imports exactly the bundle checked: refused (409) when the digest differs, (422, with the plan) when a check refuses. */
  quickbooksImport: (bundle: unknown, digest: string) =>
    request<{ result: ImportResult }>('/quickbooks/import', { method: 'POST', body: JSON.stringify({ bundle, digest }) }),

  /** 0090: a fiscal year's labels, archived ones included and marked; no year is the business's current one. */
  listLabels: (year?: number) => request<LabelYear>(`/labels${searchSuffix({ year: year === undefined ? undefined : String(year) })}`),
  createLabel: (body: { fiscalYear: number; name: string; group?: string | null; colour?: LabelColour }) =>
    request<{ label: Label }>('/labels', { method: 'POST', body: JSON.stringify(body) }),
  /** Rename, regroup, recolour, archive or restore; refused once the label's year is closed. */
  updateLabel: (id: string, changes: { name?: string; group?: string | null; colour?: LabelColour; archived?: boolean }) =>
    request<{ label: Label }>(`/labels/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(changes) }),
  /** Each chosen label of `fromYear` copied into the year after, naming the one it came from. */
  rollOverLabels: (fromYear: number, labelIds: string[]) =>
    request<{ fiscalYear: number; labels: Label[] }>('/labels/roll-over', { method: 'POST', body: JSON.stringify({ fromYear, labelIds }) }),
  /** One label's lines and its one total; `family` reads every year it was rolled over through. */
  labelReport: (labelId: string, family: boolean, from: string, to: string) =>
    request<LabelReport>(`/reports/labels${searchSuffix({ labelId, family: String(family), from, to })}`),

  /** 0086: the goods a business keeps; archived ones only when asked for. */
  listItems: (includeArchived = false) =>
    request<{ items: Item[] }>(`/items${includeArchived ? '?includeArchived=true' : ''}`),
  createItem: (body: ItemBody) => request<{ item: Item }>('/items', { method: 'POST', body: JSON.stringify(body) }),
  updateItem: (id: string, body: ItemBody) =>
    request<{ item: Item }>(`/items/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** 0086 (§7): each pool on a date, and the tie to each stock account. */
  stockOnHand: (asOf: string) => request<StockOnHand>(`/inventory${searchSuffix({ asOf })}`),
  /** 0086 (§14.5): what taking these goods on this date would cost; refused, in a sentence, when the pool is short. */
  inventoryPreview: (body: { itemId: string; accountId: string; fundId?: string | null; projectId?: string | null; quantity: string; date: string }) =>
    request<InventoryPreview>('/inventory/preview', { method: 'POST', body: JSON.stringify(body) }),
  /** 0088 (§15.2): the counts recorded, newest first, each with the pools it counted. */
  listCounts: () => request<{ counts: StockCount[] }>('/inventory/counts'),
  /** 0088: every pool of a stock account on a date, what the books hold, and a found unit's cost. */
  countSheet: (accountId: string, date: string) =>
    request<{ pools: CountSheetPool[] }>(`/inventory/counts/sheet${searchSuffix({ accountId, date })}`),
  /** 0088: a count recorded and its differences posted in one entry. */
  postCount: (body: CountBody) =>
    request<{ count: { countId: string; headerId: string | null } }>('/inventory/counts', { method: 'POST', body: JSON.stringify(body) }),
  /** 0088 (§15.6): every movement of an item in a period, and each pool's running figures. */
  itemHistory: (itemId: string, from: string, to: string) =>
    request<ItemHistory>(`/items/${encodeURIComponent(itemId)}/history${searchSuffix({ from, to })}`),
  /** 0088 (§15.10): what could still come back from a posted sale, or go back to the vendor from a bill. */
  returnableGoods: (id: string) => request<ReturnableGoods>(`/journals/${encodeURIComponent(id)}/returnable-goods`),
  /** 0088 (§15.5): goods a sale took out, brought back at the cost they left at. */
  goodsReturn: (saleId: string, body: { date: string; lines: Array<{ outLineId: string; quantity: string }> }) =>
    request<{ entry: { headerId: string } }>(`/journals/${encodeURIComponent(saleId)}/goods-return`, { method: 'POST', body: JSON.stringify(body) }),
  /** 0088 (§15.4): goods sent back against a bill, out of that bill's own layers. */
  vendorReturn: (billId: string, body: { date: string; lines: Array<{ billLineId: string; quantity: string }> }) =>
    request<{ entry: { headerId: string } }>(`/journals/${encodeURIComponent(billId)}/vendor-return`, { method: 'POST', body: JSON.stringify(body) }),

  createProject: (project: ProjectInput) =>
    request<{ project: Project }>('/projects', { method: 'POST', body: JSON.stringify(project) }),

  /** The whole project. A program's function is refused once it has a posted line. */
  updateProject: (id: string, project: ProjectInput) =>
    request<{ project: Project }>(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(project) }),

  archiveProject: (id: string) => request<{ project: Project }>(`/projects/${id}/archive`, { method: 'POST', body: '{}' }),

  restoreProject: (id: string) => request<{ project: Project }>(`/projects/${id}/restore`, { method: 'POST', body: '{}' }),

  /** 0091: the business's phases, each before the ones under it, with its path. */
  listPhases: () => request<{ phases: Phase[] }>('/phases'),
  createPhase: (phase: PhaseInput) => request<{ phase: Phase }>('/phases', { method: 'POST', body: JSON.stringify(phase) }),
  updatePhase: (id: string, phase: PhaseInput) =>
    request<{ phase: Phase }>(`/phases/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(phase) }),
  archivePhase: (id: string) => request<{ phase: Phase }>(`/phases/${encodeURIComponent(id)}/archive`, { method: 'POST', body: '{}' }),
  restorePhase: (id: string) => request<{ phase: Phase }>(`/phases/${encodeURIComponent(id)}/restore`, { method: 'POST', body: '{}' }),
  /** By phase for the period, narrowed to a project and everything under it when one is given; 404 with no phase. */
  phaseReport: (from: string, to: string, projectId?: string) =>
    request<PhaseReport>(`/reports/phases${searchSuffix({ from, to, projectId })}`),

  /** A business's funds (migration 0079); none, and `usesFunds` false, for one that keeps none. */
  listFunds: () => request<{ usesFunds: boolean; funds: Fund[] }>('/funds'),

  createFund: (fund: FundInput) => request<{ fund: Fund }>('/funds', { method: 'POST', body: JSON.stringify(fund) }),

  /** The whole fund, as the form holds it. The restriction is refused once the fund has a posted line. */
  updateFund: (id: string, fund: FundInput) =>
    request<{ fund: Fund }>(`/funds/${id}`, { method: 'PUT', body: JSON.stringify(fund) }),

  /** Archived: kept with its lines, offered for no new one. The default fund is never archived. */
  archiveFund: (id: string) => request<{ fund: Fund }>(`/funds/${id}/archive`, { method: 'POST', body: '{}' }),

  restoreFund: (id: string) => request<{ fund: Fund }>(`/funds/${id}/restore`, { method: 'POST', body: '{}' }),

  /** Closes this business (migration 0074): its name typed, and the authenticator's code. Ends every session in it. */
  closeBusiness: (name: string, code: string) =>
    request<{ status: 'closed'; closedAt: string }>('/business/close', {
      method: 'POST',
      body: JSON.stringify({ name, code }),
    }),

  /** Replaces all seven; a blank clears one. The answer is what the server now holds, normalised. */
  setBusinessDetails: (details: BusinessDetails) =>
    request<{ details: BusinessDetails }>('/business/details', {
      method: 'PUT',
      body: JSON.stringify(details),
    }),

  /** §18.6 — whether a payroll run posts its entry or leaves a draft. */
  getPayrollSettings: () => request<PayrollSettings>('/payroll/settings'),

  setPayrollSettings: (autoPost: boolean) =>
    request<PayrollSettings>('/payroll/settings', {
      method: 'PUT',
      body: JSON.stringify({ autoPost }),
    }),

  /** Stage 3 — what an employee is paid, taking effect on its own date. Never edited. */
  recordPayRate: (employeeId: string, body: RecordPayRateBody) =>
    request<{ payRate: PayRate }>(`/payroll/employees/${employeeId}/pay-rates`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** A pay rate entered by mistake. Refused once a run was paid at it. */
  removePayRate: (id: string) => request<void>(`/payroll/pay-rates/${id}`, { method: 'DELETE' }),

  /** A year's totals from pay the module did not run (§18.10 item 5). */
  recordOpeningTotals: (employeeId: string, body: RecordOpeningTotalsBody) =>
    request<{ openingTotals: OpeningTotals }>(`/payroll/employees/${employeeId}/opening-totals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Opening totals entered by mistake. Refused once a run counted from them. */
  removeOpeningTotals: (id: string) => request<void>(`/payroll/opening-totals/${id}`, { method: 'DELETE' }),

  /** §18.2 — the business's Texas unemployment rate, by year. */
  listTexasUnemploymentRates: () =>
    request<{ rates: TexasUnemploymentRate[] }>('/payroll/texas-unemployment-rates'),

  recordTexasUnemploymentRate: (year: number, rateMillionths: number) =>
    request<{ rate: TexasUnemploymentRate }>('/payroll/texas-unemployment-rates', {
      method: 'POST',
      body: JSON.stringify({ year, rateMillionths }),
    }),

  removeTexasUnemploymentRate: (year: number) =>
    request<void>(`/payroll/texas-unemployment-rates/${year}`, { method: 'DELETE' }),

  /** §18.5 — the federal deposit schedules the business recorded, the latest taking effect first. */
  listDepositSchedules: () => request<{ schedules: DepositScheduleEntry[] }>('/payroll/deposit-schedules'),

  recordDepositSchedule: (effectiveOn: string, schedule: DepositSchedule) =>
    request<{ schedule: DepositScheduleEntry }>('/payroll/deposit-schedules', {
      method: 'POST',
      body: JSON.stringify({ effectiveOn, schedule }),
    }),

  removeDepositSchedule: (effectiveOn: string) =>
    request<void>(`/payroll/deposit-schedules/${effectiveOn}`, { method: 'DELETE' }),

  /** §18.5, §18.7 — every payroll tax deposit, planned by the API from the books. */
  getDepositTracker: () => request<{ tracker: DepositTracker }>('/payroll/deposits'),

  /** §18.7 — every active run in a period; without dates, the quarter the business is in. */
  getPayrollRegister: (from?: string, to?: string) => {
    const query = new URLSearchParams();
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    const suffix = query.toString();
    return request<{ register: PayrollRegister }>(`/payroll/register${suffix ? `?${suffix}` : ''}`);
  },

  /**
   * §18.7 — the Form 941 worksheet; without a quarter, the one whose return is
   * being prepared. `quarters` is the server's list to choose from, newest first.
   */
  getForm941: (year?: number, quarter?: number) => {
    const query = new URLSearchParams();
    if (year !== undefined) query.set('year', String(year));
    if (quarter !== undefined) query.set('quarter', String(quarter));
    const suffix = query.toString();
    return request<{ report: Form941Report; quarters: WorksheetQuarter[] }>(`/payroll/forms/941${suffix ? `?${suffix}` : ''}`);
  },

  /** §18.7 — the TWC quarterly worksheet; without a quarter, the one whose report is being prepared. */
  getTwcWorksheet: (year?: number, quarter?: number) => {
    const query = new URLSearchParams();
    if (year !== undefined) query.set('year', String(year));
    if (quarter !== undefined) query.set('quarter', String(quarter));
    const suffix = query.toString();
    return request<{ report: TwcReport; quarters: WorksheetQuarter[] }>(`/payroll/forms/twc${suffix ? `?${suffix}` : ''}`);
  },

  /**
   * §18.7 — the W-2/W-3 worksheet; without a year, the one whose forms are
   * being prepared: last year's until they are due, then this year's.
   */
  getW2Worksheet: (year?: number) =>
    request<{ report: W2Report }>(`/payroll/forms/w2${year === undefined ? '' : `?year=${year}`}`),

  /**
   * §18.7 — the Form 940 worksheet; without a year, the one whose return is
   * being prepared: last year's until it is due, then this year's.
   */
  getForm940Worksheet: (year?: number) =>
    request<{ report: Form940Report }>(`/payroll/forms/940${year === undefined ? '' : `?year=${year}`}`),

  /**
   * The To Do list and the Home tab's count, in one read so the two cannot
   * disagree (apps/api/src/todo/list.ts decides both).
   */
  getTodo: () => request<{ todo: TodoList }>('/todo'),

  /**
   * Mark Filed: the operator's record that a return went in. Files nothing.
   * Payroll's returns go to payroll's route; a non-profit's annual return to its
   * own, recorded by whoever closes periods (0078).
   */
  /** 0089 (§B.19): a time-restricted fund's remaining balance released and the fund archived; `close_periods`. */
  releaseFund: (fundId: string, date: string) =>
    request<{ release: { headerId: string; amountCents: number } }>(`/funds/${encodeURIComponent(fundId)}/release`, {
      method: 'POST',
      body: JSON.stringify({ date }),
    }),
  recordReturnFiling: (body: {
    form: ReturnForm;
    year: number;
    quarter: 1 | 2 | 3 | 4 | null;
    filedOn: string;
    note?: string | null;
  }) =>
    request<{ filing: ReturnFiling }>(`${filingsPath(body.form)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** The Undo: the record stays, withdrawn, and the return is on the list again. */
  withdrawReturnFiling: (id: string, form: ReturnForm) =>
    request<{ filing: ReturnFiling }>(`${filingsPath(form)}/${encodeURIComponent(id)}/withdraw`, { method: 'POST' }),

  /** §18.7 — every employee's year to date; without a year, the business's own. */
  getYearToDate: (year?: number) =>
    request<{ report: YearToDateReport }>(`/payroll/year-to-date${year === undefined ? '' : `?year=${year}`}`),

  /** §18.6 — codes the chosen Mercury debit as one deposit's payment, split as the books accrued it. */
  codeTaxPayment: (input: { obligationKey: string; bankDebitHeaderId: string }) =>
    request<{ headerId: string; postRefusal: string | null; tracker: DepositTracker }>('/payroll/deposits/payments', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** §18.6 — every run, the latest pay date first. */
  listPayrollRuns: () => request<{ runs: PayrollRunSummary[] }>('/payroll/runs'),

  /** Everything a run would store and book, storing and booking nothing. */
  previewPayrollRun: (body: RunRequestBody) =>
    request<{ preview: RunPreview }>('/payroll/runs/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Stores a run and books it: coding the Mercury debit chosen, or a manual draft
   * from the bank account chosen. `postRefusal` says why an entry the business
   * posts automatically was left a draft.
   */
  createPayrollRun: (body: CreateRunBody) =>
    request<CreatedRun>('/payroll/runs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getPayrollRun: (id: string) => request<{ run: PayrollRunDetail }>(`/payroll/runs/${id}`),

  /** §18.7 — the run's earnings statement (Texas Labor Code §62.003). */
  getEarningsStatement: (id: string) =>
    request<{ statement: EarningsStatement }>(`/payroll/runs/${id}/statement`),

  /** Before the entry is posted. */
  voidPayrollRun: (id: string, reason: string) =>
    request<{ run: PayrollRunDetail }>(`/payroll/runs/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** After the entry is posted, through §9.6's reversal. */
  reversePayrollRun: (id: string, reason: string) =>
    request<{ run: PayrollRunDetail }>(`/payroll/runs/${id}/reverse`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** A manual draft exchanged for the Mercury debit that paid it. */
  matchPayrollRun: (id: string, bankDebitHeaderId: string) =>
    request<CreatedRun>(`/payroll/runs/${id}/match`, {
      method: 'POST',
      body: JSON.stringify({ bankDebitHeaderId }),
    }),

  // ------------------------------------------------------ bank reconciliation

  /** Every active bank-role account, with the date each is reconciled through. */
  listReconcilableAccounts: () =>
    request<{ accounts: ReconcilableAccount[] }>('/reconciliations/accounts'),

  /** An account's reconciliations, newest first, voided ones included. */
  listReconciliations: (accountId: string) =>
    request<{ reconciliations: Reconciliation[] }>(`/reconciliations${searchSuffix({ accountId })}`),

  /**
   * The three balances and what explains the difference. Writes nothing.
   * `statementBalanceCents` is LEDGER SIGN, debit minus credit: the screen
   * converts what the operator typed by the account's normal balance.
   */
  previewReconciliation: (accountId: string, through: string, statementBalanceCents?: number) =>
    request<ReconciliationPreview>(
      `/reconciliations/preview${searchSuffix({ accountId, through, statementBalanceCents })}`,
    ),

  /** Refused with `not_balanced` (422) unless the books equal the statement. */
  completeReconciliation: (body: {
    accountId: string;
    statementEndDate: string;
    statementBalanceCents: number;
  }) =>
    request<{ reconciliation: Reconciliation }>('/reconciliations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** An account's bank statements, newest statement date first. */
  listStatements: (accountId: string) =>
    request<{ statements: BankStatement[] }>(`/reconciliations/statements${searchSuffix({ accountId })}`),

  /** Every month for one account, with its statement and its reconciliation. */
  listStatementMonths: (accountId: string) =>
    request<{ months: StatementMonth[] }>(
      `/reconciliations/months${searchSuffix({ accountId })}`,
    ),

  /**
   * Downloads from Mercury every statement not already held.
   *
   * Idempotent: the second press downloads nothing. What comes back is a count
   * of what happened plus the accounts that could not be asked — the credit
   * card is the standing example, and it is reported rather than hidden.
   */
  fetchMercuryStatements: (accountId?: string) =>
    request<MercuryFetchResult>('/reconciliations/statements/fetch', {
      method: 'POST',
      body: JSON.stringify(accountId === undefined ? {} : { accountId }),
    }),

  /**
   * The same three steps as a receipt: presign, POST the file to storage with
   * `uploadToStorage`, then record. One statement per account and date; a
   * second is refused with `already_exists` before anything is uploaded.
   */
  presignStatement: (body: StatementUploadBody) =>
    request<{ upload: PresignedUpload }>('/reconciliations/statements/presign', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  recordStatement: (body: StatementUploadBody & { key: string }) =>
    request<{ statement: BankStatement }>('/reconciliations/statements', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  statementUrl: (id: string, disposition: Disposition = 'inline') =>
    request<{ url: string; filename: string; disposition: Disposition }>(
      `/reconciliations/statements/${id}/url?disposition=${disposition}`,
    ),

  /** Refused with `reconciled` while a live reconciliation for its date exists. */
  removeStatement: (id: string) =>
    request<void>(`/reconciliations/statements/${id}`, { method: 'DELETE' }),

  /** Only the account's latest can be undone, and a reason is required. */
  voidReconciliation: (id: string, reason: string) =>
    request<void>(`/reconciliations/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * §6.4 — the accrual-to-cash conversion, for the reporting screen.
   *
   * Both dates are required and there is no default period. §6.9 makes the
   * fiscal year a tenant setting, so a client-side guess at "this year" would
   * be a different claim on a tenant whose year does not start in January.
   *
   * This can fail with `unapplied_control_line` at 409, which is not a bug and
   * not a bad request: it means a deposit inside the period has not been
   * matched yet, and the report cannot be computed without silently losing
   * that amount. The screen shows the server's sentence, which names what to
   * go and match.
   */
  /**
   * §16.2 — what a batch WOULD code, writing nothing.
   *
   * The confirmation shows this, so the operator answers against real lines
   * rather than a count. The server builds it with the same code the real run
   * uses, so the preview cannot describe something else.
   */
  planBulkCoding: (headerIds: string[]) =>
    request<BulkPlan>('/journals/bulk-code', {
      method: 'POST',
      body: JSON.stringify({ headerIds, dryRun: true }),
    }),

  /** §16.2 — code the batch, and post it when asked. */
  /**
   * `excludeCounterparts`: also exclude the draft other side of each transfer
   * coded. `excludeDuplicates`: exclude, instead of coding, each entry whose
   * other side is already posted. Both are the confirmation's checkboxes (§9.1).
   */
  applyBulkCoding: (
    headerIds: string[],
    post: boolean,
    options: {
      excludeCounterparts: boolean;
      excludeDuplicates: boolean;
      /** Only the memos the operator changed, by header id. An emptied memo is null. */
      memos?: Record<string, string | null>;
    } = {
      excludeCounterparts: false,
      excludeDuplicates: false,
    },
  ) =>
    request<BulkResult>('/journals/bulk-code', {
      method: 'POST',
      body: JSON.stringify({ headerIds, post, ...options }),
    }),

  /** §9.1 — the other side of a transfer, or null. Writes nothing. */
  findCounterpart: async (id: string) =>
    (await request<{ counterpart: Counterpart } | undefined>(
      `/journals/${id}/counterpart`,
    )) ?? null,

  /**
   * §9.1 — exclude this header and record the entry that accounts for the
   * movement. The server re-derives the pairing rather than trusting the id.
   */
  recordCounterpart: (id: string, counterpartHeaderId: string) =>
    request<{ headerId: string; relatedHeaderId: string }>(`/journals/${id}/counterpart`, {
      method: 'POST',
      body: JSON.stringify({ counterpartHeaderId }),
    }),

  cashBasisReport: (from: string, to: string) =>
    request<CashBasisReport>(`/reports/cash-basis${searchSuffix({ from, to })}`),

  /*
   * §9.9 — the three statements.
   *
   * `from` is optional on the trial balance alone, where leaving it out means
   * since the books began. The balance sheet takes no `from` at all: assets as
   * at a date are cumulative, and a windowed balance sheet is a category error
   * the API refuses to make expressible.
   */
  trialBalance: (to: string, from?: string) =>
    request<TrialBalanceReport>(
      `/reports/trial-balance${searchSuffix(from ? { from, to } : { to })}`,
    ),

  incomeStatement: (from: string, to: string) =>
    request<IncomeStatementReport>(`/reports/income-statement${searchSuffix({ from, to })}`),

  balanceSheet: (asOf: string) =>
    request<BalanceSheetReport>(`/reports/balance-sheet${searchSuffix({ asOf })}`),

  /** A non-profit's statements (N3); 404 for any other business. */
  statementOfActivities: (from: string, to: string) =>
    request<StatementOfActivities>(`/reports/activities${searchSuffix({ from, to })}`),

  statementOfFinancialPosition: (asOf: string) =>
    request<StatementOfFinancialPosition>(`/reports/financial-position${searchSuffix({ asOf })}`),

  fundBalances: (from: string, to: string) =>
    request<FundBalancesReport>(`/reports/fund-balances${searchSuffix({ from, to })}`),

  /** A non-profit's Statement of Functional Expenses (N4b); 404 for any other business. */
  functionalExpenses: (from: string, to: string) =>
    request<FunctionalExpensesReport>(`/reports/functional-expenses${searchSuffix({ from, to })}`),

  /** Revenue, expenses and cost to date by project (N4b); 404 for a business with no project. */
  /** 0088 (§15.7): goods distributed by program; 404 until the business has a project. */
  goodsByProgram: (from: string, to: string) => request<GoodsByProgram>(`/reports/goods-by-program${searchSuffix({ from, to })}`),
  projectReport: (from: string, to: string) =>
    request<ProjectReport>(`/reports/projects${searchSuffix({ from, to })}`),

  /**
   * Officer wages against distributions, and stock basis in Form 7203's order
   * (apps/api/src/services/shareholder.ts). Without a year, the business's own.
   */
  getShareholder: (year?: number) =>
    request<{ report: ShareholderReport }>(`/reports/shareholder${year === undefined ? '' : `?year=${year}`}`),

  /** 0096: a partnership's partners, their accounts and every set of their shares. */
  listPartners: () => request<PartnersList>('/partners'),

  /** 0096: a partner, made with their Capital, Contributions and Draws accounts. */
  addPartner: (name: string, partyId: string | null = null) =>
    request<{ partner: Partner }>('/partners', { method: 'POST', body: JSON.stringify({ name, partyId }) }),

  /** 0096: the partners' shares from a date, written whole, replacing whatever that date held. */
  /** 0097: a partner's party, or none (round 193's TW-2). */
  setPartnerParty: (partnerId: string, partyId: string | null) =>
    request<{ partner: Partner }>(`/partners/${partnerId}`, { method: 'PATCH', body: JSON.stringify({ partyId }) }),

  recordPartnerShares: (set: PartnerShareSet) =>
    request<PartnersList>('/partners/shares', { method: 'PUT', body: JSON.stringify(set) }),

  /** 0096: a partnership's partners' capital for one fiscal year, the current one by default. */
  getPartnersCapital: (year?: number) =>
    request<{ statement: PartnersCapitalStatement }>(`/reports/partners-capital${year === undefined ? '' : `?year=${year}`}`),

  /** 0094: a business taxed with its owner's equity for one fiscal year, the current one by default. */
  getOwnersEquity: (year?: number) =>
    request<{ statement: OwnersEquityStatement }>(`/reports/owners-equity${year === undefined ? '' : `?year=${year}`}`),

  /**
   * §9.9 — the drill-down: the posted lines behind one account's figure.
   * `from` omitted means everything through `to`, which is what a balance
   * sheet row is. The net equals the report row's `netCents` by construction.
   */
  /** No `to` is the account register: every posted line, whatever its date. */
  accountLines: (accountId: string, to?: string, from?: string, options?: { closing?: 'exclude' | 'before' }) =>
    request<AccountLinesReport>(
      `/reports/account-lines${searchSuffix({
        ...(to ? (from ? { accountId, from, to } : { accountId, to }) : { accountId }),
        // Which closing entries the lines leave out (0052): every one for the
        // income statement, the one dated on `to` for the trial balance.
        ...(options?.closing ? { closing: options.closing } : {}),
      })}`,
    ),

  /**
   * §9.9 — the books as an .xlsx file, for the page's period.
   *
   * Not `request`, which parses JSON: this reads the bytes. A refusal is JSON and
   * is read the same way `request` reads one, so the screen gets the server's
   * sentence either way.
   */
  exportBooks: (to: string, from?: string): Promise<Blob> =>
    fetchFile(`/reports/books.xlsx${searchSuffix(from ? { from, to } : { to })}`),

  /**
   * The IRS's Form 941 for a quarter, filled from its worksheet (issue #43).
   * Bytes; a refusal (a draft run, a changed IRS file, the IRS unreachable)
   * comes back as the server's sentence.
   */
  downloadForm941Pdf: (year: number, quarter: number): Promise<Blob> =>
    fetchFile(`/payroll/forms/941.pdf${searchSuffix({ year: String(year), quarter: String(quarter) })}`),

  /** Issue #49 — the Form 1099-NEC worksheet for a year, or the year being prepared. */
  getForm1099Nec: (year?: number) =>
    request<{ report: Form1099NecReport }>(`/payroll/forms/1099-nec${year === undefined ? '' : `?year=${year}`}`),

  /** Issue #49 — one recipient's Form 1099-NEC, the IRS's PDF filled: Copy B, never Copy A. */
  downloadForm1099NecPdf: (year: number, partyId: string): Promise<Blob> =>
    fetchFile(`/payroll/forms/1099-nec.pdf${searchSuffix({ year: String(year), partyId })}`),

  /** Issue #46 — the year's Form 940, the IRS's PDF filled from the worksheet. */
  downloadForm940Pdf: (year: number): Promise<Blob> =>
    fetchFile(`/payroll/forms/940.pdf${searchSuffix({ year: String(year) })}`),

  /** Issue #46 — one employee's Form W-2, the IRS's PDF filled: Copies B, C, 2 and D, never Copy A. */
  downloadW2Pdf: (year: number, employeeId: string): Promise<Blob> =>
    fetchFile(`/payroll/forms/w2.pdf${searchSuffix({ year: String(year), employeeId })}`),

  // ------------------------------------------------------------ coding rules

  listCodingRules: (includeInactive = false) =>
    request<{ rules: CodingRule[] }>(
      `/coding-rules${includeInactive ? '?includeInactive=true' : ''}`,
    ),

  /**
   * §9.7 step 1 — the primary suggestion for one counterparty.
   *
   * Returns null when nothing matches. The server answers 204 for that, and
   * `request` turns a 204 into undefined, so the coalesce here is what makes
   * "no rule" a value a screen can hold rather than an exception it has to
   * catch. No rule is the ordinary case.
   *
   * This never writes, and that is now the only thing true of it that used to
   * be a rule. §9.7 said suggestions are never auto-applied; the operator
   * withdrew that on 2026-09-12, the editor fills an uncoded entry from a
   * matched rule on open, and `planBulkCoding`/`applyBulkCoding` below read
   * rules and code entries from them wholesale. This call stays a pure read
   * because a screen asking "what would this counterparty code to" is a
   * different question from "do it".
   */
  matchCodingRule: async (counterparty: string) =>
    (await request<{ suggestion: RuleSuggestion } | undefined>(
      `/coding-rules/match${searchSuffix({ counterparty })}`,
    )) ?? null,

  createCodingRule: (body: CreateCodingRuleBody) =>
    request<{ rule: CodingRule }>('/coding-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateCodingRule: (id: string, body: UpdateCodingRuleBody) =>
    request<{ rule: CodingRule }>(`/coding-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteCodingRule: (id: string) =>
    request<void>(`/coding-rules/${id}`, { method: 'DELETE' }),

  /* ----------------------------------- §6.10 recurring commitments (0037) */

  /** One tab's worth. `active` when not asked, which is the live list. */
  listCommitments: (state: CommitmentState = 'active') =>
    request<{ commitments: RecurringCommitment[] }>(`/commitments?state=${state}`),

  /** Patterns the operator has refused. */
  listIgnoredPatterns: () =>
    request<{ ignored: IgnoredPattern[] }>('/commitments/ignored'),

  /** Stops a detected pattern being offered. Idempotent; 204, no body. */
  ignoreCommitmentPattern: (counterparty: string, accountId: string) =>
    request<void>('/commitments/ignored', {
      method: 'POST',
      body: JSON.stringify({ counterparty, accountId }),
    }),

  /** Offers it again. */
  unignoreCommitmentPattern: (id: string) =>
    request<void>(`/commitments/ignored/${id}`, { method: 'DELETE' }),

  /** What was expected and has not been seen. An empty list is good news. */
  listMissedCommitments: () =>
    request<{ missed: MissedCommitment[] }>('/commitments/missed'),

  /** Regular payments no commitment covers. Offered, never recorded. */
  listCommitmentSuggestions: () =>
    request<{ candidates: CommitmentCandidate[] }>('/commitments/suggestions'),

  recordCommitment: (body: RecordCommitmentBody) =>
    request<{ commitment: RecurringCommitment }>('/commitments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * The only change a commitment allows. There is no `updateCommitment` on
   * purpose: migration 0037 refuses every other edit, so a method that could
   * ask would only ever produce a refusal.
   */
  endCommitment: (id: string, endsOn: string) =>
    request<{ commitment: RecurringCommitment }>(`/commitments/${id}/end`, {
      method: 'POST',
      body: JSON.stringify({ endsOn }),
    }),

  /**
   * For one entered wrongly. One that really ran is ENDED, not removed.
   *
   * A real delete and unrecoverable, so the screen confirms before calling it.
   */
  removeCommitment: (id: string) =>
    request<void>(`/commitments/${id}`, { method: 'DELETE' }),
};

/** §6.10 — a payment the operator states leaves on a schedule (0037). */
/** 0093 (#69): how closely a commitment's payment keeps the schedule's day. */
export type CommitmentTiming = 'on_day' | 'in_month';

export interface RecurringCommitment {
  id: string;
  label: string;
  partyId: string | null;
  partyName: string | null;
  counterparty: string | null;
  accountId: string;
  accountCode: string;
  accountName: string;
  amountCents: number;
  cadence: PayFrequency;
  /** 0093 (#69): kept on its day, or paid anywhere in its month. */
  timing: CommitmentTiming;
  startsOn: string;
  endsOn: string | null;
  /** Derived from the dates, never stored. See migration 0037. */
  isActive: boolean;
  recordedAt: string;
  /** When it was withdrawn as entered wrongly, or null (migration 0038). */
  /** Which tab it belongs in. Derived from the dates, never stored. */
  state: CommitmentState;
}

/**
 * The two states a commitment can be in.
 *
 * A third, `removed`, was built on 2026-09-17 and taken out the same day:
 * REMOVE means the commitment should never have been recorded, so the history
 * it would keep is history that was wrong. Migration 0038's header argues it.
 */
export type CommitmentState = 'active' | 'ended';

/** A detected pattern the operator has said is not a standing order. */
export interface IgnoredPattern {
  id: string;
  counterparty: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  ignoredAt: string;
}

export interface RecordCommitmentBody {
  label: string;
  partyId?: string | null;
  counterparty?: string | null;
  accountId: string;
  amountCents: number;
  cadence: PayFrequency;
  /** `on_day` when absent. */
  timing?: CommitmentTiming;
  startsOn: string;
  endsOn?: string | null;
}

/** An expectation with nothing plausible against it. */
export interface MissedCommitment {
  commitmentId: string;
  label: string;
  /** How it was judged: near the date, or anywhere in its month (0093). */
  timing: CommitmentTiming;
  expectedOn: string;
  expectedCents: number;
  /**
   * What WAS found near the date, if anything. A payment for a different
   * amount is not a miss -- it is a payment to look at, and reporting it as
   * missing would send the operator hunting a transaction that is right there.
   */
  seen: { headerId: string; entryDate: string; amountCents: number } | null;
}

/** A regular payment the transactions suggest, which nobody has recorded. */
export interface CommitmentCandidate {
  /** `in_month`: the same amount each month on any day (#69). */
  timing: CommitmentTiming;
  counterparty: string;
  partyId: string | null;
  accountId: string;
  accountCode: string;
  accountName: string;
  amountCents: number;
  occurrences: number;
  dayOfMonth: number;
  /** How far from that day it has ever strayed. A weekend is 2; noise is 20. */
  daySpread: number;
  firstSeen: string;
  lastSeen: string;
}

/**
 * Mirrors GET /accounts.
 *
 * The balance fields are bigint on the server and arrive as STRINGS. They are
 * typed as such rather than quietly coerced, because the editor does not need
 * them and a `number` here would be a lie that only shows up in arithmetic.
 */
/**
 * §6.10 — creating an account.
 *
 * NO `accountRole`. The API accepts it and defaults it to 'normal'; nothing in
 * this application sends one, and that is deliberate rather than unfinished.
 *
 * `account_role` is reserved for §6.2's cash-basis conversion: it marks the
 * A/R and A/P control accounts whose lines get suppressed and re-derived from
 * their clearings, and the deferred-revenue account. Choosing it wrongly does
 * not fail -- it silently changes what a tax-basis report says, months later,
 * with nothing raising. It is not a field to offer beside "Name" on a form
 * filled in mid-coding.
 *
 * `normalBalance` IS asked for, and defaulted from the type rather than
 * derived and hidden. Assets and expenses are debit-normal, everything else
 * credit-normal, which is right until it is not: accumulated depreciation is
 * an asset with a credit balance. Getting it wrong flips the sign of the
 * account everywhere it is displayed, so the operator sees the answer and can
 * disagree with it.
 */
export interface CreateJournalBody {
  /** The invoice date, YYYY-MM-DD. */
  entryDate: string;
  partyId?: string | null;
  counterparty?: string | null;
  memo?: string | null;
  /** The invoice number. */
  invoiceReference?: string | null;
}

export interface CreateAccountBody {
  code: string;
  name: string;
  type: Account['type'];
  normalBalance: Account['normalBalance'];
  parentAccountId?: string | null;
  /** 0081: an asset of the normal role that holds a project's cost. */
  tracksProjects?: boolean;
  /** 0084: a gift's kind on revenue, `pledge` on a receivable. */
  contributionKind?: ContributionKind | null;
  /** 0086: `stock` on an asset of the normal role, `cost_of_sales` on an expense. */
  inventoryRole?: InventoryRole | null;
}

/**
 * `type` and `normalBalance` are absent, matching the server's updateSchema.
 * Changing either on an account that already carries entries reverses the sign
 * of every historical balance and can move the account between the balance
 * sheet and the P&L. The correction is a new account and a reclassifying
 * entry, which leaves an audit trail.
 */
export interface UpdateAccountBody {
  code?: string;
  name?: string;
  parentAccountId?: string | null;
  isActive?: boolean;
  tracksProjects?: boolean;
  contributionKind?: ContributionKind | null;
  /** 0086: null clears it. Fixed once the account has lines. */
  inventoryRole?: InventoryRole | null;
}

/** Migration 0084 (§B.17.3): what an account holds of a non-profit's donors' giving. */
export type ContributionKind = 'cash' | 'in_kind' | 'event' | 'pledge';

export interface Account {
  id: string;
  code: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  accountRole: string;
  normalBalance: 'debit' | 'credit';
  /** §7 — the bank mapping. Null on every account that is not one. */
  mercuryAccountId: string | null;
  parentAccountId: string | null;
  isActive: boolean;
  isSeeded: boolean;
  /** 0081: an asset that holds a project's cost, so its lines may carry a project. Absent on the tests' hand-made accounts. */
  tracksProjects?: boolean;
  /** 0084: a gift's kind on revenue, `pledge` on a receivable; null or absent holds no giving. */
  contributionKind?: ContributionKind | null;
  /** 0086: `stock` holds goods, `cost_of_sales` takes a sale's cost; null or absent neither. */
  inventoryRole?: InventoryRole | null;
  balanceCents: string;
  postedLineCount: number;
  rollupBalanceCents: string;
  childCount: number;
}

/**
 * §7 — a customer, vendor, employee or donee.
 *
 * Roles are independent flags rather than one field: a party can be several at
 * once, and an employee is specifically NOT a 1099 vendor.
 */
export interface Party {
  id: string;
  name: string;
  /** Journal headers pointing at this party — what deactivating would affect. */
  entryCount: number;
  isCustomer: boolean;
  isVendor: boolean;
  isEmployee: boolean;
  isCharity: boolean;
  /** 0084 (§B.17): gives to this non-profit. Absent on the tests' hand-made parties. */
  isDonor?: boolean;
  /*
   * WHETHER there is a tax id, never the tax id. The value is a TIN or an SSN,
   * encrypted at rest under PII_ENCRYPTION_KEY, and the API deliberately does
   * not return it on a read -- otherwise every party response, browser cache
   * and captured payload would carry one for a value no screen displays.
   */
  hasTaxId: boolean;
  email: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  notes: string | null;
  isActive: boolean;
  /** Migration 0060: the operator reports this vendor on Form 1099-NEC (issue #49). */
  receives1099Nec: boolean;
  /** 0085 (§B.18): a private foundation's disqualified person. Absent on the tests' hand-made parties. */
  isDisqualifiedPerson?: boolean;
  /** How they are one; set exactly when the flag is. */
  disqualifiedRelationship?: DisqualifiedRelationship | null;
}

/** IRC 4946's ways of being a disqualified person, as migration 0085 holds them. */
export type DisqualifiedRelationship = 'founder' | 'substantial_contributor' | 'foundation_manager' | 'family_member' | 'controlled_entity';

/**
 * Every field optional: the parties screen sends one change at a time, so a
 * role toggle does not restate the name and cannot overwrite a rename made in
 * another tab between load and click.
 */
export interface Attachment {
  id: string;
  headerId: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  /**
   * False only when the entry is posted AND its period is closed. Since
   * migration 0027 a posted entry in an open period gives up a receipt, and
   * the removal is recorded in its history.
   */
  deletable: boolean;
}

/**
 * How the browser is told to treat a signed GET. Mirrors the API's enum.
 *
 * Two names for what looks like one operation, because they are two: saving a
 * receipt and looking at one are different requests for the same object, and
 * asking for the wrong one fails silently in one direction.
 */
export type Disposition = 'attachment' | 'inline';

export interface PresignBody {
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** The form S3 wants POSTed. `fields` go in the body, before the file. */
export interface PresignedUpload {
  url: string;
  fields: Record<string, string>;
  key: string;
}

/**
 * Uploads straight to S3 with the presigned form.
 *
 * Deliberately NOT through `request`: this is a cross-origin POST to AWS, so
 * it must not carry our session cookie or the X-Requested-With header, and its
 * response is XML rather than JSON. Routing it through the API client would
 * attach all three.
 */
export async function uploadToStorage(upload: PresignedUpload, file: File): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(upload.fields)) form.append(key, value);
  // The file goes LAST. S3 ignores anything after the file part.
  form.append('file', file);

  /*
   * The rejection and the refusal are DIFFERENT failures and used to read the
   * same.
   *
   * fetch() rejects -- it does not resolve with a status -- when the browser
   * blocks the exchange: no network, or the storage bucket refusing the
   * origin. That rejection escaped as a bare TypeError, missed the
   * `instanceof ApiError` branch in the caller, and surfaced as the generic
   * "Could not attach that file."
   *
   * That is what happened on 2026-09-09: the bucket had no CORS configuration
   * at all, so S3 answered every browser upload with 403 CORSResponse and the
   * screen said nothing useful about it. Naming the two cases apart is what
   * makes the next one diagnosable from the screen instead of from the AWS
   * CLI.
   */
  let response: Response;
  try {
    response = await fetch(upload.url, { method: 'POST', body: form });
  } catch {
    throw new ApiError(0, 'upload_unreachable', {
      serverMessage:
        'The file never reached storage. Check the connection; if it persists the storage bucket is refusing this site.',
    });
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'upload_failed', {
      serverMessage: 'Storage refused the file. Try again.',
    });
  }
}

export interface UpdatePartyBody {
  name?: string;
  isCustomer?: boolean;
  isVendor?: boolean;
  isEmployee?: boolean;
  isCharity?: boolean;
  isDonor?: boolean;
  notes?: string | null;
  isActive?: boolean;
  /** Issue #49: written, never read back -- the API answers `hasTaxId` only. */
  taxId?: string | null;
  receives1099Nec?: boolean;
  /** 0085: a private foundation's only. Clearing the flag clears the relationship. */
  isDisqualifiedPerson?: boolean;
  disqualifiedRelationship?: DisqualifiedRelationship | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
}

/** GET /payroll/forms/1099-nec (apps/api/src/services/form-1099-nec.ts). A worksheet, never a filing. */
export interface Form1099NecReport {
  year: number;
  /** Box 1a's threshold for the year; null when the application does not know it. */
  thresholdCents: number | null;
  due: { date: string; statutory: string };
  recipients: Array<{
    partyId: string;
    name: string;
    hasTaxId: boolean;
    address: string[];
    paidCents: number;
    /** Money in from the same party: shown, never subtracted. */
    receivedCents: number;
    entries: number;
    required: boolean | null;
  }>;
  unmarked: Array<{ partyId: string; name: string; paidCents: number }>;
}

export interface CreatePartyBody {
  name: string;
  isCustomer?: boolean;
  isVendor?: boolean;
  isEmployee?: boolean;
  isCharity?: boolean;
  isDonor?: boolean;
}

export interface SaveLinesBody {
  expectedVersion: number;
  /**
   * Absent means leave alone, null means clear, a value means set. The three
   * states are distinct at the API, so do not collapse them by always sending
   * every field.
   *
   * mercuryTransactionId, mercuryAccountId and bankAmountCents are absent on
   * purpose: they are what the bank reported and what §9.3's check measures
   * the operator's coding against. Sending one is a 400 naming an unknown key.
   */
  header?: {
    entryDate?: string;
    cashDate?: string | null;
    counterparty?: string | null;
    memo?: string | null;
    invoiceReference?: string | null;
    /** Migration 0036: absent leaves it, null clears it. */
    dueDate?: string | null;
    /** Migration 0084: what a donor received in return for an event gift; absent leaves it, null clears it. */
    goodsServicesCents?: number | null;
    /**
     * §7. `counterparty` is absent from what the editor sends: that column
     * holds what the bank reported and is provenance now. The party is the
     * operator's classification, and the two answer different questions.
     */
    partyId?: string | null;
  };
  /**
   * `id` is the line's own id, for a line the entry already has. On a POSTED
   * entry it is what makes a save update that line in place; without it every
   * line reads as removed and re-added, which is refused for any line that was
   * ever matched to a payment. A draft ignores it. A new line has none.
   */
  lines: Array<{
    id?: string;
    accountId: string;
    debitCents: number;
    creditCents: number;
    fundId?: string;
    projectId?: string | null;
    /** Migration 0091: the line's phase; null is none, chosen. */
    phaseId?: string | null;
    /** Migration 0086: the item and its quantity, on a stock line or a sale's revenue line. */
    itemId?: string | null;
    quantity?: string | null;
    /** Migration 0086: on a cost line, the index in `lines` of the stock line whose cost it is. */
    costsLine?: number;
    /** Migration 0090: the line's labels. Absent leaves a kept line's as they are. */
    labelIds?: string[];
  }>;
}

/** Migration 0086 (the inventory plan's §3.4). */
export type InventoryRole = 'stock' | 'cost_of_sales';

/** GET /items (0086, §14.4): the goods a business keeps. */
export interface Item {
  id: string;
  name: string;
  sku: string | null;
  unit: string;
  defaultStockAccountId: string | null;
  defaultCostAccountId: string | null;
  defaultRevenueAccountId: string | null;
  archivedAt: string | null;
  /** On hand across every pool, as the decimal string the database holds. */
  onHand: string;
  valueCents: number;
  hasLines: boolean;
}

export interface ItemBody {
  name?: string;
  sku?: string | null;
  unit?: string;
  defaultStockAccountId?: string | null;
  defaultCostAccountId?: string | null;
  defaultRevenueAccountId?: string | null;
  archived?: boolean;
}

/** GET /inventory (0086, §7): each pool's quantity and value as of a date, and the tie to each stock account. */
export interface StockOnHand {
  asOf: string;
  pools: Array<{
    itemId: string;
    item: string;
    sku: string | null;
    unit: string;
    accountId: string;
    accountCode: string;
    accountName: string;
    fundId: string | null;
    fundName: string | null;
    projectId: string | null;
    projectName: string | null;
    quantity: string;
    valueCents: number;
  }>;
  accounts: Array<{ accountId: string; code: string; name: string; balanceCents: number; poolsCents: number }>;
}

/** POST /inventory/preview (0086, §14.5): what taking goods would cost; not a promise. */
export interface InventoryPreview {
  totalCents: number;
  draws: Array<{ inDetailId: string; quantity: string; cents: number }>;
}

/** 0088 (§15.2): one pool of a count sheet. */
export interface CountSheetPool {
  itemId: string;
  item: string;
  unit: string;
  fundId: string | null;
  fundName: string | null;
  projectId: string | null;
  projectName: string | null;
  onHand: string;
  valueCents: number;
  /** The latest layer's cost of one unit, in cents: what goods found over are valued at. Null when none arrived. */
  unitCostCents: number | null;
}

export interface CountBody {
  countedOn: string;
  adjustmentAccountId: string;
  note?: string | null;
  lines: Array<{ itemId: string; accountId: string; fundId?: string | null; projectId?: string | null; counted: string; unitCostCents?: number }>;
}

/** 0088: a count recorded; its entry may since have been reversed, and the count stays. */
export interface StockCount {
  id: string;
  countedOn: string;
  adjustmentAccountId: string;
  headerId: string | null;
  reversed: boolean;
  note: string | null;
  lines: Array<{ itemId: string; item: string; unit: string; accountId: string; fundId: string | null; projectId: string | null; onHand: string; counted: string; detailId: string | null }>;
}

export type MovementKind = 'received' | 'out' | 'sold' | 'count' | 'transfer' | 'returned' | 'returned_to_vendor' | 'reversal';

export interface HistoryPool {
  accountId: string;
  accountCode: string;
  fundId: string | null;
  fundName: string | null;
  projectId: string | null;
  projectName: string | null;
}

/** 0088 (§15.6): an item's movements in a period, each pool's figures before it, and after each line. */
export interface ItemHistory {
  itemId: string;
  item: string;
  unit: string;
  from: string;
  to: string;
  opening: Array<HistoryPool & { onHand: string; valueCents: number }>;
  rows: Array<
    HistoryPool & {
      headerId: string;
      date: string;
      lineId: string;
      kind: MovementKind;
      direction: 'in' | 'out';
      quantity: string;
      cents: number;
      otherAccount: string | null;
      onHand: string;
      valueCents: number;
    }
  >;
}

/** 0088 (§15.10): what could still come back from a sale, or go back to a vendor from a bill. */
export interface ReturnableGoods {
  kind: 'sale' | 'bill' | null;
  lines: Array<{ lineId: string; itemId: string; item: string; unit: string; quantity: string; left: string }>;
}

/** 0088 (§15.7): goods distributed by program, then item, on each expense account. */
export interface GoodsByProgram {
  from: string;
  to: string;
  programs: Array<{
    projectId: string | null;
    projectName: string | null;
    totalCents: number;
    rows: Array<{ itemId: string; item: string; unit: string; accountId: string; accountCode: string; accountName: string; quantity: string; cents: number }>;
  }>;
  totalCents: number;
}

/**
 * §6.3 — one payment match on an entry's line, phrased from THIS entry's side.
 * Mirrors GET /journals/:id/matches and `detail.matches` on a refused save.
 */
export interface EntryMatch {
  /** What voidClearing takes. */
  clearingId: string;
  amountCents: number;
  createdAt: string;
  voided: boolean;
  /** The line on this entry the match hangs off. */
  lineId: string;
  lineNumber: number;
  /** The entry on the other side of the match. */
  otherHeaderId: string;
  otherLineId: string;
  otherEntryDate: string;
  otherCounterparty: string | null;
  otherInvoiceReference: string | null;
}

/** §5.7 — one recorded event. `payload` is shaped by `action`; see 0027. */
export interface EntryHistoryEvent {
  id: string;
  at: string;
  action: string;
  actorUserId: string | null;
  /** Null for a change made outside the application, such as psql. */
  actorEmail: string | null;
  payload: Record<string, unknown>;
}

/** One save's worth of events. Order WITHIN `events` is not meaningful. */
export interface EntryChange {
  /** Null for events written before migration 0027. */
  txid: string | null;
  at: string;
  actorEmail: string | null;
  events: EntryHistoryEvent[];
}

/** §6.9 — what a close through `through` would leave behind. */
export interface BooksLockPreview {
  through: string;
  /** Bank transactions nobody has coded, which could never be posted. */
  uncodedDraftCount: number;
  /** Coded drafts, one step from posting, which could never take it. */
  codedDraftCount: number;
  /** Posted entries that would become fixed. */
  postedEntryCount: number;
}

/**
 * Bank reconciliation. EVERY AMOUNT HERE IS LEDGER SIGN, debit minus credit.
 * On a credit-normal account (the card) a balance owed is negative; the screen
 * shows and accepts it in the account's normal sign and converts at the edge.
 */
export interface ReconcilableAccount {
  accountId: string;
  code: string;
  name: string;
  normalBalance: 'debit' | 'credit';
  /** Null when no Mercury account is mapped. A feed may still come from imported bank files. */
  mercuryAccountId: string | null;
  reconciledThrough: string | null;
  reconciledBalanceCents: number | null;
}

export interface Reconciliation {
  id: string;
  accountId: string;
  statementEndDate: string;
  statementBalanceCents: number;
  bookBalanceCents: number;
  feedBalanceCents: number | null;
  createdAt: string;
  createdByEmail: string | null;
  voidedAt: string | null;
  voidedByEmail: string | null;
  voidedReason: string | null;
}

/** The bank's statement for one account and statement date. */
export interface BankStatement {
  id: string;
  accountId: string;
  statementEndDate: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedByEmail: string | null;
  /** False while a live reconciliation for this date exists: it is the evidence. */
  removable: boolean;
}

export interface StatementUploadBody {
  accountId: string;
  statementEndDate: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** In the bank feed, not in the books. */
export interface UnbookedTransaction {
  headerId: string;
  date: string;
  counterparty: string | null;
  bankAmountCents: number;
  status: string;
  uncoded: boolean;
  /** An excluded transfer whose other side is not posted yet. */
  transferPending: boolean;
}

/** In the books, not from this account's feed. */
export interface OffFeedLine {
  headerId: string;
  date: string;
  counterparty: string | null;
  memo: string | null;
  amountCents: number;
}

export interface ReconciliationPreview {
  account: ReconcilableAccount;
  through: string;
  statementBalanceCents: number | null;
  bookBalanceCents: number;
  feedBalanceCents: number | null;
  /** statement - books; null until a statement balance is given. */
  differenceCents: number | null;
  unbooked: UnbookedTransaction[];
  unbookedTotalCents: number;
  offFeed: OffFeedLine[];
  offFeedTotalCents: number;
  /**
   * A live reconciliation ends on exactly `through`. An earlier date than the
   * account's latest is NOT this: it is open, and checks a balance the later
   * reconciliation skipped over (0051).
   */
  alreadyReconciled: boolean;
  today: string;
}

/** Mirrors GET and PUT /books/lock. */
/** Mirrors POST /journals/:id/reverse. */
export interface ReversalResult {
  reversalHeaderId: string;
  reversesHeaderId: string;
  /** The tenant's today: the date the correction actually took. */
  entryDate: string;
  lineCount: number;
  /** Migration 0090: labels the reversal's lines do not carry, having no counterpart in its year. Absent on older fixtures. */
  labelsLeftOff?: { lineNumber: number; label: string; fiscalYear: number }[];
}

/** Mirrors GET /books/lock/history. */
export interface BooksLockChange {
  id: string;
  at: string;
  action: 'books_closed' | 'books_reopened';
  from: string | null;
  to: string | null;
  actorEmail: string | null;
}

/** Mirrors GET /sync/exceptions. */
export interface SyncException {
  id: string;
  headerId: string | null;
  mercuryTransactionId: string | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  detectedAt: string;
  entryDate: string | null;
  counterparty: string | null;
}

export interface BooksLock {
  lockedThrough: string | null;
  /** The TENANT's today, not the browser's. A lock date is a business date. */
  today: string;
  preview: BooksLockPreview | null;
}

// ---------------------------------------------------------------- payroll

/** §18.3 — Step 1(c)'s three boxes, as the API stores them. */
export type FilingStatus =
  | 'single_or_married_separately'
  | 'married_jointly_or_surviving_spouse'
  | 'head_of_household';

/**
 * What is on file for an SSN. Never the number: at most its last four digits,
 * and `unreadable` when the server holds no key for the version it names.
 */
export type SsnOnFile =
  | { status: 'none' }
  | { status: 'on_file'; lastFour: string }
  /** On file, and its digits not shown to this user (0069: view_tax_ids). */
  | { status: 'withheld' }
  | { status: 'unreadable'; keyVersion: number | null };

/** Mirrors a W-4 from the API: a field per line of the form. */
export interface W4 {
  id: string;
  employeeId: string;
  formYear: number;
  exempt: boolean;
  filingStatus: FilingStatus | null;
  multipleJobs: boolean;
  step3Cents: number;
  step4aCents: number;
  step4bCents: number;
  step4cCents: number;
  signedOn: string;
  effectiveOn: string;
  recordedAt: string;
  recordedByEmail: string | null;
}

/** Mirrors GET /payroll/employees. */
export interface EmployeeSummary {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  suffix: string | null;
  hireDate: string;
  isOwnerOfficer: boolean;
  /** 0083: an officer; the owner-officer always is. A non-profit pays its officers to Officer Compensation. */
  isOfficer: boolean;
  hasSsn: boolean;
  /** The W-4 in effect on the tenant's today. */
  currentW4: { id: string; effectiveOn: string } | null;
}

/** Mirrors GET /payroll/employees/:id. */
export interface Employee extends EmployeeSummary {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  ssn: SsnOnFile;
  /** Every W-4, the latest effective date first. */
  w4s: W4[];
  /** Every pay rate, the latest taking effect first. */
  payRates: PayRate[];
  /** Opening totals by year, the latest year first. */
  openingTotals: OpeningTotals[];
  /** 0083: how their pay is split across funds and programs; empty is all to the defaults. */
  allocation: AllocationShare[];
  /** The TENANT's today, which decided `currentW4`. */
  today: string;
}

export interface CreateEmployeeBody {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  suffix?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  hireDate: string;
  isOwnerOfficer: boolean;
  /** 0083: absent is not an officer, unless the owner-officer. */
  isOfficer?: boolean;
  ssn?: string | null;
}

/**
 * Migration 0083 — one share of an employee's pay (§B.16.3), in basis points:
 * 10,000 is all of it. A null fund is the default fund; a null project is
 * none, or a non-profit's default program.
 */
export interface AllocationShare {
  fundId: string | null;
  fundCode: string | null;
  projectId: string | null;
  projectName: string | null;
  shareBp: number;
  /** Archived since: the next run for this employee is refused until it is changed. */
  fundArchived: boolean;
  projectArchived: boolean;
}

export interface ShareInput {
  fundId: string | null;
  projectId: string | null;
  shareBp: number;
}

export type UpdateEmployeeBody = Partial<CreateEmployeeBody>;

export interface RecordW4Body {
  formYear: number;
  exempt: boolean;
  filingStatus: FilingStatus | null;
  multipleJobs: boolean;
  step3Cents: number;
  step4aCents: number;
  step4bCents: number;
  step4cCents: number;
  signedOn: string;
  effectiveOn: string;
}

/** §18.6 — per business. False: every payroll run is saved as a draft. */
export interface PayrollSettings {
  autoPost: boolean;
}

/** §18.4's four. Quarterly only for the owner-officer. */
export type PayFrequency = 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

/** Mirrors a pay rate: a salary per pay period, taking effect on its own date. */
export interface PayRate {
  id: string;
  employeeId: string;
  /** The first pay date paid at this rate. */
  effectiveOn: string;
  frequency: PayFrequency;
  amountPerPeriodCents: number;
  recordedAt: string;
  recordedByEmail: string | null;
}

export interface RecordPayRateBody {
  effectiveOn: string;
  frequency: PayFrequency;
  amountPerPeriodCents: number;
}

/** A year's totals from pay the module did not run. Wages are before any limit. */
export interface OpeningTotalsAmounts {
  federalIncomeTaxWagesCents: number;
  federalIncomeTaxWithheldCents: number;
  socialSecurityWagesCents: number;
  socialSecurityTaxCents: number;
  medicareWagesCents: number;
  medicareTaxCents: number;
  additionalMedicareTaxCents: number;
  futaWagesCents: number;
  texasUnemploymentWagesCents: number;
  healthPremiumCents: number;
  supplementalWagesCents: number;
}

export interface OpeningTotals extends OpeningTotalsAmounts {
  id: string;
  employeeId: string;
  year: number;
  /** The last pay date the totals include. */
  throughDate: string;
  recordedAt: string;
  recordedByEmail: string | null;
}

export interface RecordOpeningTotalsBody extends OpeningTotalsAmounts {
  year: number;
  throughDate: string;
}

/** §18.5 — the IRS assigns one of the two for a calendar year (migration 0048). */
export type DepositSchedule = 'monthly' | 'semiweekly';

/** Mirrors a schedule from GET /payroll/deposit-schedules. */
export interface DepositScheduleEntry {
  /** The first pay date it decides. */
  effectiveOn: string;
  schedule: DepositSchedule;
  recordedAt: string;
  recordedByEmail: string | null;
}

/**
 * §18.5, §18.7 — the deposit tracker, as GET /payroll/deposits returns it
 * (apps/api/src/payroll/deposits.ts). Planned from the LEDGER: an accrual is a
 * net credit an entry made to one of the five payroll liability accounts, a
 * payment a net debit, and payments go to the oldest liability on the same
 * account. The browser plans nothing; it shows the plan.
 */
export type PayrollTax = 'federal' | 'futa' | 'texas';

export type LiabilityRole =
  | 'federal_income_tax_withheld'
  | 'social_security_tax_payable'
  | 'medicare_tax_payable'
  | 'futa_tax_payable'
  | 'texas_unemployment_tax_payable';

export type DepositBasis =
  | 'monthly'
  | 'semiweekly'
  | 'next_day'
  | 'with_return'
  | 'no_schedule'
  | 'futa_quarter'
  | 'futa_year'
  | 'texas_quarter';

export type DepositStatus = 'paid' | 'due' | 'overdue' | 'undated';

export interface DepositAccrual {
  headerId: string;
  role: LiabilityRole;
  /** When the wages were paid: the run's pay date, or the entry's date. */
  payDate: string;
  cents: number;
  runId: string | null;
  posted: boolean;
}

export interface DepositAllocation {
  /** The payment's entry. */
  headerId: string;
  /** The bank's date where the entry has one. */
  paidOn: string;
  role: LiabilityRole;
  cents: number;
  posted: boolean;
  counterparty: string | null;
}

/**
 * §18.7 — the payroll register, as GET /payroll/register returns it
 * (apps/api/src/services/payroll-register.ts). Every figure is one the run
 * stored; the sums are the server's.
 */
export interface RegisterAmounts {
  grossPayCents: number;
  /** A 2% shareholder's health premiums: in income tax wages, not paid in cash. */
  healthPremiumCents: number;
  federalIncomeTaxWagesCents: number;
  federalIncomeTaxWithheldCents: number;
  socialSecurityEmployeeCents: number;
  /** Without Additional Medicare Tax, which is its own figure. */
  medicareEmployeeCents: number;
  additionalMedicareEmployeeCents: number;
  netPayCents: number;
  socialSecurityEmployerCents: number;
  medicareEmployerCents: number;
  futaEmployerCents: number;
  texasUnemploymentEmployerCents: number;
}

export interface RegisterRun extends RegisterAmounts {
  runId: string;
  employeeId: string;
  employeeName: string;
  payDate: string;
  entry: { headerId: string; status: 'draft' | 'posted' } | null;
}

export interface RegisterTotals extends RegisterAmounts {
  runCount: number;
}

export interface PayrollRegister {
  from: string;
  to: string;
  runs: RegisterRun[];
  employees: Array<{ employeeId: string; employeeName: string; totals: RegisterTotals }>;
  totals: RegisterTotals;
  /** Opening totals whose stretch, 1 January to their date, meets the period: pay with no rows here. */
  handEntered: Array<{ employeeId: string; employeeName: string; year: number; throughDate: string }>;
  /** The year to date through the period's end; null when that year has no tax figures. */
  yearToDate: YearToDateReport | null;
  /** Why there is no year to date, in the server's words. */
  yearToDateRefusal: string | null;
}

/**
 * A quarter a worksheet can be read for, as apps/api/src/payroll/deadlines.ts's
 * offeredQuarters lists them: from the business's current quarter back through
 * the first of last year, and the one on show.
 */
export interface WorksheetQuarter {
  year: number;
  quarter: 1 | 2 | 3 | 4;
  start: string;
  end: string;
  label: string;
}

/**
 * §18.7 — the Form 941 worksheet, as GET /payroll/forms/941 returns it
 * (apps/api/src/services/form-941.ts). The lines are the server's; this screen
 * lays them out. A worksheet, never a filing.
 */
export interface Form941TaxLine {
  wagesCents: number;
  taxCents: number;
}

export interface Form941Worksheet {
  quarter: string;
  line1: number;
  line2: number;
  line3: number;
  line4: boolean;
  line5a: Form941TaxLine;
  line5b: Form941TaxLine;
  line5c: Form941TaxLine;
  line5d: Form941TaxLine;
  line5e: number;
  line5f: number;
  line6: number;
  line7: number;
  line8: number;
  line9: number;
  line10: number;
  line11: number;
  line12: number;
  line13: number;
  line14: number;
  line15a: number;
  line16: {
    box: 'de_minimis' | 'monthly' | 'semiweekly' | null;
    recordRequired: boolean;
    months: [number, number, number];
    days: Array<{ date: string; cents: number }>;
    total: number;
  };
}

export interface Form941Run {
  runId: string;
  employeeId: string;
  employeeName: string;
  payDate: string;
  incomeTaxWagesCents: number;
  incomeTaxWithheldCents: number;
  socialSecurityWagesCents: number;
  medicareWagesCents: number;
  additionalMedicareWagesCents: number;
  socialSecurityTaxCents: number;
  medicareTaxCents: number;
  additionalMedicareTaxCents: number;
}

export interface Form941Report {
  quarter: { year: number; quarter: 1 | 2 | 3 | 4; start: string; end: string; label: string };
  worksheet: Form941Worksheet;
  runs: Form941Run[];
  draftRuns: Array<{ runId: string; payDate: string; employeeName: string }>;
  deposits: Array<{ headerId: string; paidOn: string; cents: number; counterparty: string | null; posted: boolean }>;
  books: { ledgerLiabilityCents: number; runsLiabilityCents: number };
  schedule: { semiweekly: boolean | null; nextDay: boolean; priorQuarter: string; priorQuarterLiabilityCents: number | null };
  unappliedFederalCents: number;
}

/**
 * §18.7 — one run's pay stub, as apps/api/src/payroll/earnings-statement.ts
 * builds it from Texas Labor Code §62.003. The server arranges it and checks
 * it adds up; the screen lays it out and prints it.
 */
export interface EarningsStatement {
  employer: string;
  employee: string;
  payDate: string;
  payPeriod: { start: string; end: string; basis: 'calendar' | 'ending_on_pay_date' };
  rate: { frequency: PayFrequency; amountPerPeriodCents: number };
  earnings: Array<{ label: string; cents: number }>;
  totalEarnedCents: number;
  deductions: Array<{ label: string; purpose: string; cents: number }>;
  totalDeductionsCents: number;
  netPayCents: number;
  /** Reported as wages and not paid: a 2% shareholder's health premium. */
  reportedNotPaid: Array<{ label: string; cents: number }>;
  withdrawn: { status: 'voided' | 'reversed'; on: string | null; reason: string | null } | null;
}

/**
 * §18.7 — the TWC quarterly worksheet, as GET /payroll/forms/twc returns it
 * (apps/api/src/services/twc-quarterly.ts). No Social Security number is in it.
 */
export interface TwcReport {
  quarter: { year: number; quarter: 1 | 2 | 3 | 4; start: string; end: string; label: string };
  worksheet: {
    quarter: string;
    totalWagesCents: number;
    taxableWagesCents: number;
    rateMillionths: number | null;
    taxDueCents: number | null;
    taxBookedCents: number;
    employment: [number, number, number];
    employees: Array<{
      employeeId: string;
      employeeName: string;
      grossWagesCents: number;
      taxableWagesCents: number;
      months: [boolean, boolean, boolean];
    }>;
  };
  draftRuns: Array<{ runId: string; payDate: string; employeeName: string }>;
  obligation: {
    owedCents: number;
    paidCents: number;
    outstandingCents: number;
    dueDate: string | null;
    payments: Array<{ headerId: string; paidOn: string; cents: number; counterparty: string | null; posted: boolean }>;
  } | null;
  books: { ledgerCents: number; runsCents: number };
}

/**
 * §18.7 — one employee's year, or every employee's added together, as
 * apps/api/src/payroll/year-to-date.ts works it out: the opening totals and the
 * year's posted runs, wages subject to a base stopped at it.
 */
export interface YearToDateFigures {
  grossPayCents: number;
  supplementalWagesCents: number;
  /** A 2% shareholder's health premiums: in the income tax wages, never paid in cash. */
  healthPremiumCents: number;
  incomeTaxWagesCents: number;
  incomeTaxWithheldCents: number;
  socialSecurityWagesCents: number;
  socialSecurityTaxCents: number;
  medicareWagesCents: number;
  /** Without Additional Medicare Tax, which is apart. */
  medicareTaxCents: number;
  additionalMedicareTaxCents: number;
  futaTaxableWagesCents: number;
  texasUnemploymentTaxableWagesCents: number;
  netPayCents: number;
}

/** §18.7 — GET /payroll/year-to-date (apps/api/src/services/year-to-date.ts). No Social Security number is in it. */
export interface YearToDateReport {
  year: number;
  /** Newest first: the years with pay recorded, the business's own, and this one. */
  years: number[];
  /** The last day counted, when the year to date stops short of the year's end; null for the whole year. */
  through: string | null;
  employees: Array<{
    employeeId: string;
    employeeName: string;
    /** The opening totals' last pay date, or null when the year has none for this employee. */
    openingThrough: string | null;
    /** Posted runs counted. */
    runs: number;
    figures: YearToDateFigures;
  }>;
  /** Opening totals recorded through a date after `through`: one total that cannot be divided at it, so not counted. */
  openingNotSplit: Array<{ employeeId: string; employeeName: string; throughDate: string }>;
  /** Runs whose entries are drafts: listed, not counted. */
  draftRuns: Array<{ runId: string; payDate: string; employeeName: string }>;
  totals: YearToDateFigures;
}

/** Boxes 1 to 6 of a Form W-2, or of Form W-3 as their totals (apps/api/src/payroll/w2.ts). */
export interface W2Boxes {
  box1Cents: number;
  box2Cents: number;
  box3Cents: number;
  box4Cents: number;
  box5Cents: number;
  box6Cents: number;
}

/** One employee's Form W-2, as the server fills it. No Social Security number: box a is the employee's page. */
export interface W2Form {
  employeeId: string;
  /** The whole name, for a heading. */
  employeeName: string;
  /** Box e's three sub-boxes: first name and middle initial, surname, suffix. */
  nameBoxes: { firstNameAndInitial: string; lastName: string; suffix: string | null };
  /** Box f: a line each; empty when no address is recorded. */
  address: string[];
  boxes: W2Boxes;
  /** Optional, labelled: a 2% shareholder's premiums, already in box 1. */
  box14a: Array<{ label: string; cents: number }>;
  /** The instructions' checks this form fails; none when it is sound. */
  checks: string[];
  openingThrough: string | null;
  runs: number;
}

/** GET and PUT /business/details (apps/api/src/services/business-details.ts). Null is not entered. */
export interface BusinessDetails {
  /** XX-XXXXXXX once saved; the server accepts it with or without the dash. */
  ein: string | null;
  legalName: string | null;
  tradeName: string | null;
  addressStreet: string | null;
  addressCity: string | null;
  /** Two capital letters once saved. */
  addressState: string | null;
  /** Five digits, or ZIP+4 with its dash, once saved. */
  addressZip: string | null;
}

/** §18.7 — GET /payroll/forms/w2 (apps/api/src/services/w2.ts). A worksheet, never a filing. */
export interface W2Report {
  year: number;
  /** Newest first: the years with pay recorded, the business's own, and this one. */
  years: number[];
  /** To the Social Security Administration and to each employee by `date`; `statutory` when a weekend moved it. */
  due: { date: string; statutory: string };
  /** False until the year is over: the figures are the year so far. */
  yearComplete: boolean;
  employer: string;
  /** Box b and W-3 box e; null until Settings holds one. */
  employerEin: string | null;
  /** Box c's address and W-3 box g, a line each; empty until Settings holds one. */
  employerAddress: string[];
  forms: W2Form[];
  transmittal: { formCount: number; boxes: W2Boxes };
  draftRuns: Array<{ runId: string; payDate: string; employeeName: string }>;
}

/** A line of Form 940's Part 5: one quarter's FUTA liability, or quarters joined where opening totals span them. */
export interface Form940Quarter {
  from: 1 | 2 | 3 | 4;
  to: 1 | 2 | 3 | 4;
  cents: number;
}

/** Form 940's lines (apps/api/src/payroll/form-940.ts). */
export interface Form940Worksheet {
  line1a: 'TX';
  /** Credit reduction applies, and Schedule A with it. */
  line2: boolean;
  line3: number;
  line4: number;
  /** Box 4a, Fringe benefits. */
  line4a: boolean;
  line5: number;
  line6: number;
  line7: number;
  line8: number;
  line9: number;
  line10: number;
  line11: number;
  line12: number;
  line13: number;
  line14: number;
  line15a: number;
  /** Part 5 is asked for only when line 12 is more than $500. */
  part5: boolean;
  line16: Form940Quarter[];
  line17: number;
}

/** One employee's part of lines 3 to 7. */
export interface Form940EmployeePart {
  employeeId: string;
  employeeName: string;
  paymentsCents: number;
  exemptCents: number;
  overBaseCents: number;
  taxableCents: number;
  openingThrough: string | null;
  runs: number;
}

/** §18.7 — GET /payroll/forms/940 (apps/api/src/services/form-940.ts). A worksheet, never a filing. */
export interface Form940Report {
  year: number;
  /** Newest first: the years with pay recorded, the business's own, and this one. */
  years: number[];
  due: { date: string; statutory: string };
  /** The later date allowed when every dollar of the year's FUTA tax was deposited when due. */
  dueIfDeposited: { date: string; statutory: string };
  /** False until the year is over: the figures are the year so far. */
  yearComplete: boolean;
  employer: string;
  worksheet: Form940Worksheet;
  employees: Form940EmployeePart[];
  draftRuns: Array<{ runId: string; payDate: string; employeeName: string }>;
  /** Line 13's payments, one per entry, as the deposit tracker applied them. */
  deposits: Array<{ headerId: string; paidOn: string; cents: number; counterparty: string | null; posted: boolean }>;
  /** The FUTA posted entries accrued for the year's pay, set beside line 12: drafts are on neither side. */
  books: { futaAccruedCents: number };
  /** Paid on the FUTA account beyond everything accrued: applied to no year. */
  unappliedFutaCents: number;
  /** For line 10: the year's Texas unemployment tax unpaid in the books, and any paid after Form 940's due date. */
  texas: { unpaidCents: number; paidLateCents: number };
}

/** §18.6 — an uncoded Mercury debit that could pay a deposit, shown beside it. */
export interface TaxDebitCandidate {
  headerId: string;
  /** The bank's date. */
  paidOn: string;
  counterparty: string | null;
  bankMemo: string | null;
  /** As the bank reported it: negative, money out. */
  amountCents: number;
  accountCode: string;
  accountName: string;
}

export interface DepositObligation {
  key: string;
  tax: PayrollTax;
  basis: DepositBasis;
  /**
   * What it is called, without its period: "Federal Tax Deposit", "FUTA".
   * Named on the server (`DUE_NAMES` in apps/api/src/payroll/deadlines.ts),
   * which the To Do list, the tracker and the forecast all read.
   */
  title: string;
  label: string;
  quarter: string;
  from: string;
  to: string;
  /** Null only when no deposit schedule is recorded for its pay dates. */
  dueDate: string | null;
  movedFrom: string | null;
  because: string;
  lines: Array<{ role: LiabilityRole; owedCents: number; paidCents: number }>;
  owedCents: number;
  paidCents: number;
  outstandingCents: number;
  status: DepositStatus;
  /** When paid: the date of the last payment applied to it. */
  paidOn: string | null;
  accruals: DepositAccrual[];
  payments: DepositAllocation[];
  /** Uncoded Mercury debits of exactly what is still owed, dated on or after its first pay. */
  bankDebits: TaxDebitCandidate[];
  /**
   * On the oldest unpaid deposit only: uncoded debits of another amount to a
   * payee this tax has been paid to. Named, never coded from here.
   */
  otherDebits: TaxDebitCandidate[];
}

export interface DepositQuarter {
  quarter: string;
  federalLiabilityCents: number;
  requirement: DepositRequirement;
  nextDay: boolean;
}

export interface LiabilityAccountPosition {
  role: LiabilityRole;
  accruedCents: number;
  paidCents: number;
  /** Accrued less paid; negative when paid more than accrued. */
  balanceCents: number;
  /** Paid beyond every accrual on the account. */
  unappliedCents: number;
}

export interface DepositTracker {
  /** The business's today, which decided due and overdue. */
  today: string;
  /** In the order they fall due; undated last. */
  obligations: DepositObligation[];
  quarters: DepositQuarter[];
  accounts: LiabilityAccountPosition[];
  /** The accounts added together, by the server: the payroll tax the books hold owing. */
  accountTotals: { accruedCents: number; paidCents: number; balanceCents: number };
  accountsByRole: Partial<Record<LiabilityRole, { code: string; name: string }>>;
}

export interface TexasUnemploymentRate {
  year: number;
  rateMillionths: number;
  /** For reading: "2.7%". */
  rate: string;
  recordedAt: string;
  recordedByEmail: string | null;
}

/** Wages earlier in the year, before any wage base: what each limit counts from. */
export interface YearToDateWages {
  socialSecurityWagesCents: number;
  medicareWagesCents: number;
  futaWagesCents: number;
  texasUnemploymentWagesCents: number;
  supplementalWagesCents: number;
}

/** Mirrors the calculation's result (apps/api/src/payroll/calculation.ts). */
export interface PayResult {
  year: number;
  grossPayCents: number;
  incomeTax: {
    wagesCents: number;
    withheldCents: number;
    worksheet: {
      adjustedAnnualWageCents: number;
      filingStatus: FilingStatus;
      step2Checkbox: boolean;
      bracketPosition: number;
    } | null;
  };
  socialSecurity: { taxableWagesCents: number; employeeCents: number; employerCents: number };
  medicare: {
    taxableWagesCents: number;
    employeeCents: number;
    employerCents: number;
    additionalTaxableWagesCents: number;
    additionalEmployeeCents: number;
  };
  futa: { taxableWagesCents: number; employerCents: number };
  texasUnemployment: { taxableWagesCents: number; employerCents: number };
  netPayCents: number;
  addsToYearToDate: YearToDateWages;
}

/** One line of a run's entry, less the bank line. */
export interface PayrollEntryLine {
  role: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debitCents: number;
  creditCents: number;
  /** 0083: a share of the wages or employer taxes, by the employee's allocation; absent otherwise. */
  fundId?: string | null;
  fundCode?: string | null;
  projectId?: string | null;
  projectName?: string | null;
}

/** An uncoded Mercury debit of exactly the net pay, near the pay date. */
export interface BankDebitCandidate {
  headerId: string;
  entryDate: string;
  counterparty: string | null;
  bankMemo: string | null;
  /** Signed as the bank reports it: money out is negative. */
  amountCents: number;
  accountCode: string;
  accountName: string;
  daysFromPayDate: number;
}

export interface RunRequestBody {
  employeeId: string;
  payDate: string;
  /** Left out for the pay rate's amount. */
  regularWagesCents?: number;
  supplemental?: { wagesCents: number; method: 'flat' | 'aggregate' } | null;
  healthPremiumCents?: number;
}

export interface CreateRunBody extends RunRequestBody {
  bankDebitHeaderId?: string | null;
  bankAccountId?: string | null;
}

/** Mirrors POST /payroll/runs/preview. */
export interface RunPreview {
  employee: { id: string; name: string; isOwnerOfficer: boolean };
  payDate: string;
  taxYear: number;
  payRate: { id: string; effectiveOn: string; frequency: PayFrequency; amountPerPeriodCents: number };
  w4: {
    id: string;
    effectiveOn: string;
    formYear: number;
    exempt: boolean;
    filingStatus: FilingStatus | null;
    multipleJobs: boolean;
  };
  texasUnemploymentRateMillionths: number;
  openingTotals: { id: string; throughDate: string } | null;
  regularWagesCents: number;
  supplemental: { wagesCents: number; method: 'flat' | 'aggregate' } | null;
  healthPremiumCents: number;
  yearToDate: YearToDateWages;
  result: PayResult;
  lines: PayrollEntryLine[];
  employerTaxesCents: number;
  /** §18.5 — what this pay would owe the IRS, summed by the server. */
  form941LiabilityCents: number;
  /** §18.5 — what it would make due, tested on its quarter with the runs already in it. */
  deadlines: PayDeadlines;
  bankDebits: BankDebitCandidate[];
}

/**
 * §18.5 — what a pay makes due, computed by the API (src/payroll/deadlines.ts).
 *
 * THE RULE LIVES ON THE SERVER, ONCE. The browser computed these dates itself
 * until payroll stage 4 (2026-09-22) moved them: the deposit tracker needs them
 * where the ledger and the deposit schedule are, and a second copy here would
 * drift from it. Nothing in this app derives a statutory date any more.
 */
export type DeadlineKind = 'federal_deposit' | 'form_941' | 'texas_wage_report' | 'form_940';

export interface Deadline {
  kind: DeadlineKind;
  label: string;
  /** The date it is due, after any weekend or holiday shift. */
  date: string;
  /** The statutory date, when a weekend or holiday moved it. */
  movedFrom: string | null;
  because: string;
}

/**
 * How the $2,500 test came out for the quarter: at or above it; under it so
 * far, with the quarter still open, so still deposited on schedule; or ended
 * under it, so the tax may go with the return.
 */
export type DepositRequirement = 'at_or_above' | 'under_so_far' | 'ended_under';

export interface PayDeadlines {
  /** "Q3 2026". */
  quarter: string;
  quarterLiabilityCents: number;
  thresholdCents: number;
  requirement: DepositRequirement;
  /** The schedule recorded as in effect on the pay date; null when none is, and then the deposit has no date. */
  schedule: DepositSchedule | null;
  items: Deadline[];
}

export type PayrollRunStatus = 'active' | 'voided' | 'reversed';

/** Mirrors GET /payroll/runs. */
export interface PayrollRunSummary {
  id: string;
  employeeId: string;
  employeeName: string;
  payDate: string;
  taxYear: number;
  grossPayCents: number;
  netPayCents: number;
  status: PayrollRunStatus;
  entry: { headerId: string; source: 'mercury' | 'manual' | 'bank_feed'; status: JournalStatus } | null;
  /**
   * A manual entry made before the bank debit existed, not yet exchanged for it,
   * and still paying the net pay from a bank account. The server decides.
   */
  awaitingBankDebit: boolean;
  /**
   * §18.5 — income tax withheld plus BOTH halves of Social Security and
   * Medicare: this run's share of the quarter's figure the $2,500 deposit
   * threshold is tested against. FUTA and Texas unemployment are deliberately
   * excluded; they are a different return.
   */
  form941LiabilityCents: number;
  /** §18.5 — what this pay makes due; null once voided or reversed, when it owes nothing. */
  deadlines: PayDeadlines | null;
  createdAt: string;
}

/** Mirrors GET /payroll/runs/:id. */
export interface PayrollRunDetail extends PayrollRunSummary {
  incomeTaxWithheldCents: number;
  socialSecurityEmployeeCents: number;
  socialSecurityEmployerCents: number;
  medicareEmployeeCents: number;
  medicareEmployerCents: number;
  additionalMedicareEmployeeCents: number;
  futaEmployerCents: number;
  texasUnemploymentEmployerCents: number;
  payRate: { id: string; effectiveOn: string; frequency: PayFrequency; amountPerPeriodCents: number };
  w4: { id: string; effectiveOn: string; formYear: number };
  payPeriodsPerYear: number;
  regularWagesCents: number;
  supplementalWagesCents: number;
  supplementalMethod: 'flat' | 'aggregate' | null;
  healthPremiumCents: number;
  texasUnemploymentRateMillionths: number;
  openingTotalsId: string | null;
  yearToDate: YearToDateWages;
  incomeTaxWagesCents: number;
  socialSecurityTaxableWagesCents: number;
  medicareTaxableWagesCents: number;
  additionalMedicareTaxableWagesCents: number;
  futaTaxableWagesCents: number;
  texasUnemploymentTaxableWagesCents: number;
  employerTaxesCents: number;
  voidedAt: string | null;
  voidedByEmail: string | null;
  voidReason: string | null;
  reversalHeaderId: string | null;
  createdByEmail: string | null;
  /** False when the entry's lines were changed in the editor after the run; null with no entry. */
  entryMatchesRun: boolean | null;
  /** While awaiting the bank debit: the bank debits (Mercury's or imported) that could be it. */
  bankDebits: BankDebitCandidate[];
}

export interface CreatedRun {
  run: PayrollRunDetail;
  /** Why an entry the business posts automatically was left a draft; null otherwise. */
  postRefusal: string | null;
}

export interface SavedDraft {
  headerId: string;
  version: number;
  lineCount: number;
  debitTotalCents: number;
  creditTotalCents: number;
  balanced: boolean;
}

export interface PostedEntry {
  headerId: string;
  version: number;
  postedAt: string;
  debitTotalCents: number;
  creditTotalCents: number;
  lineCount: number;
}

// -------------------------------------------------------------- journals

export type JournalStatus = 'draft' | 'posted' | 'dismissed';
/** `bank_feed` is a transaction imported from a bank file (plan §G.5). */
export type JournalSource = 'manual' | 'mercury' | 'bank_feed';

export interface JournalFilters {
  status?: JournalStatus;
  /** `bank` is every row a bank reported: Mercury's and an imported file's. */
  source?: JournalSource | 'bank';
  from?: string;
  to?: string;
  /** Headers with no detail lines. Serialised as the string the API expects. */
  uncoded?: 'true' | 'false';
  q?: string;
  /**
   * Advanced search, three-valued like `uncoded` above. 'true' is the entries
   * a rule would fill, as Auto Post would code them; 'false' is the ones it
   * would not touch, which is the harder list to get at by eye.
   */
  autoFilled?: 'true' | 'false';
  /** Advanced search: 'true' has a receipt, 'false' has none. */
  hasReceipt?: 'true' | 'false';
  limit?: number;
  offset?: number;
}

/**
 * §9.9 — one account's place in a report.
 *
 * `netCents` is this account alone and `rollupCents` includes its descendants.
 * BOTH are needed and neither is redundant: a parent shows the rollup, and the
 * totals add the nets, because adding rollups as well counts every child twice
 * and leaves the report balanced and wrong.
 */
export interface ReportAccount {
  accountId: string;
  code: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  parentAccountId: string | null;
  depth: number;
  debitCents: number;
  creditCents: number;
  netCents: number;
  rollupCents: number;
  empty: boolean;
}

/** One posted line behind a report figure. */
export interface AccountLine {
  lineId: string;
  headerId: string;
  lineNumber: number;
  entryDate: string;
  counterparty: string | null;
  memo: string | null;
  debitCents: number;
  creditCents: number;
  /** Debit minus credit, cumulative through this line. */
  balanceCents: number;
}

/** Mirrors GET /reports/account-lines. Amounts are debit minus credit. */
export interface AccountLinesReport {
  account: {
    accountId: string;
    code: string;
    name: string;
    type: ReportAccount['type'];
    normalBalance: 'debit' | 'credit';
  };
  from: string | null;
  /** Null for the account register: every posted line. */
  to: string | null;
  lines: AccountLine[];
  lineCount: number;
  debitCents: number;
  creditCents: number;
  netCents: number;
  truncated: boolean;
}

/** How much of the books is not in a report yet. */
/** Mirrors `apps/api/src/services/nonprofit-statements.ts` (N3). */
export interface ClassColumns {
  withoutCents: number;
  withCents: number;
  totalCents: number;
}

export interface ActivitiesRow extends ClassColumns {
  accountId: string;
  code: string;
  name: string;
}

export interface StatementOfActivities {
  from: string;
  to: string;
  revenue: ActivitiesRow[];
  releases: ClassColumns;
  expenses: ActivitiesRow[];
  totalRevenue: ClassColumns;
  totalExpenses: ClassColumns;
  changeInNetAssets: ClassColumns;
  tiesToFunds: boolean;
  completeness: ReportCompleteness;
}

export interface FinancialPositionAccount {
  accountId: string;
  code: string;
  name: string;
  balanceCents: number;
}

export interface StatementOfFinancialPosition {
  asOf: string;
  assets: FinancialPositionAccount[];
  liabilities: FinancialPositionAccount[];
  totalAssetsCents: number;
  totalLiabilitiesCents: number;
  netAssets: ClassColumns;
  balanced: boolean;
  completeness: ReportCompleteness;
}

export interface FundBalance {
  fundId: string;
  code: string;
  name: string;
  restriction: FundRestriction;
  boardDesignated: boolean;
  archived: boolean;
  assetsCents: number;
  dueFromOtherFundsCents: number;
  liabilitiesCents: number;
  netAssetsCents: number;
  revenueCents: number;
  expensesCents: number;
  balanced: boolean;
}

/**
 * The reports a project feeds (N4b of the #55 plan, §B.15.5), shaped from
 * apps/api/src/services/project-reports.ts. Each row's columns add up to that
 * account's figure on the statement it comes from.
 */
export interface ProjectColumn {
  /** Null for the lines that name no project. */
  projectId: string | null;
  /** Its path, "Subdivision › Lot 12 › Unit B" (0091: three levels). */
  name: string;
  function: ProjectFunction | null;
  archived: boolean;
}

export interface FunctionalColumns {
  /** One per entry of `programs`, in its order. */
  programCents: number[];
  programServicesCents: number;
  managementAndGeneralCents: number;
  fundraisingCents: number;
  membershipDevelopmentCents: number;
  /** An expense line with no program, which the database refuses on a posted entry. */
  noProgramCents: number;
  totalCents: number;
}

export interface FunctionalExpenseRow extends FunctionalColumns {
  accountId: string;
  code: string;
  name: string;
}

/** A non-profit's Statement of Functional Expenses; 404 for any other business. */
export interface FunctionalExpensesReport {
  from: string;
  to: string;
  programs: ProjectColumn[];
  hasMembershipDevelopment: boolean;
  expenses: FunctionalExpenseRow[];
  total: FunctionalColumns;
  completeness: ReportCompleteness;
}

export interface ProjectReportRow {
  accountId: string;
  code: string;
  name: string;
  /** One per entry of `columns`, the last being No Project. */
  cells: number[];
  totalCents: number;
}

/** The Programs (or Projects) report; 404 for a business with no project. */
export interface ProjectReport {
  from: string;
  to: string;
  columns: ProjectColumn[];
  revenue: ProjectReportRow[];
  expenses: ProjectReportRow[];
  totalRevenue: number[];
  totalExpenses: number[];
  difference: number[];
  /** Each project-tracking asset's balance at `to`. */
  held: ProjectReportRow[];
  totalHeld: number[];
  /** Expenses through `to` plus what is held. */
  costToDate: number[];
  completeness: ReportCompleteness;
}

/** Mirrors GET /reports/phases (0091, §C.7.4): §C.3's sections by phase, flat, as the Projects report's are. */
export interface PhaseColumn {
  /** Null for No Phase. */
  phaseId: string | null;
  name: string;
  archived: boolean;
}

export interface PhaseReport extends Omit<ProjectReport, 'columns'> {
  columns: PhaseColumn[];
  /** The project it is narrowed to, with everything under it; null for every line. */
  project: { id: string; name: string } | null;
}

export interface FundBalancesReport {
  from: string;
  to: string;
  funds: FundBalance[];
  completeness: ReportCompleteness;
}

export interface ReportCompleteness {
  uncodedCount: number;
  uncodedAbsCents: number;
  codedDraftCount: number;
}

/** The closing entry a trial balance dated on its day leaves out (0052). */
export interface ClosingLeftOut {
  fiscalYear: number;
  entryDate: string;
}

export interface TrialBalanceReport {
  from: string | null;
  to: string;
  accounts: ReportAccount[];
  totalDebitCents: number;
  totalCreditCents: number;
  balanced: boolean;
  /** The closing entry dated on `to` that this report leaves out, or null (0052). */
  closingLeftOut: ClosingLeftOut | null;
  completeness: ReportCompleteness;
}

export interface IncomeStatementReport {
  from: string;
  to: string;
  revenue: ReportAccount[];
  expenses: ReportAccount[];
  totalRevenueCents: number;
  totalExpenseCents: number;
  netIncomeCents: number;
  completeness: ReportCompleteness;
}

/** Mirrors GET /fiscal-years (migration 0052). */
export interface FiscalYearClose {
  headerId: string;
  closedAt: string;
  closedByEmail: string | null;
  /** Revenue less expense the entry moved into retained earnings. Negative is a loss. */
  netIncomeCents: number;
  lineCount: number;
}

export interface FiscalYear {
  year: number;
  start: string;
  end: string;
  /** The last day has passed in the business's time zone. */
  ended: boolean;
  close: FiscalYearClose | null;
  /** Revenue less expense posted inside the year, closing entries left out. */
  netIncomeCents: number;
  uncodedDraftCount: number;
  codedDraftCount: number;
  /** Why closing would be refused now, in a sentence, or null. */
  obstacle: string | null;
}

export interface FiscalYearChange {
  id: string;
  at: string;
  action: 'fiscal_year_closed' | 'fiscal_year_reopened';
  year: number;
  netIncomeCents: number | null;
  reason: string | null;
  actorEmail: string | null;
}

export interface FiscalYears {
  fiscalYearStartMonth: number;
  today: string;
  /** A for-profit's; null for a non-profit, whose year closes to net assets. */
  retainedEarnings: { accountId: string; code: string; name: string; isActive: boolean } | null;
  /** A non-profit's two classes of net assets (N3); null for a for-profit. */
  netAssets?: {
    without: { accountId: string; code: string; name: string; isActive: boolean } | null;
    with: { accountId: string; code: string; name: string; isActive: boolean } | null;
  } | null;
  /** Newest first. */
  years: FiscalYear[];
  history: FiscalYearChange[];
}

export interface FiscalYearCloseResult {
  headerId: string;
  year: number;
  start: string;
  end: string;
  netIncomeCents: number;
  lineCount: number;
}

export interface BalanceSheetReport {
  asOf: string;
  assets: ReportAccount[];
  liabilities: ReportAccount[];
  equity: ReportAccount[];
  totalAssetsCents: number;
  totalLiabilitiesCents: number;
  totalEquityCents: number;
  /**
   * Revenue and expense posted to date that no year-end close has moved into
   * retained earnings (migration 0052). Before any close, every P&L line ever.
   */
  currentEarningsCents: number;
  /** The latest fiscal year closed on or before `asOf`, or null. */
  closedFiscalYear: number | null;
  /** That year's last day: everything through it is on retained earnings. */
  closedThrough: string | null;
  differenceCents: number;
  balanced: boolean;
  completeness: ReportCompleteness;
}

/** Mirrors GET /journals. Amounts are whole cents. */
export interface JournalListRow {
  id: string;
  entryDate: string;
  cashDate: string | null;
  status: JournalStatus;
  source: JournalSource;
  counterparty: string | null;
  memo: string | null;
  invoiceReference: string | null;
  bankAmountCents: number | null;
  mercuryCategory: string | null;
  /**
   * §9.1 — Mercury's payment kind, verbatim (migration 0023).
   *
   * Null means NOT CAPTURED, not "no rail". Every manual entry is null, and so
   * are the posted headers that predate 0023: the bank's facts on a posted
   * header are refused even since 0027 made the rest editable, so no backfill
   * can ever reach them.
   *
   * DO NOT PRESENT THIS AS "ACH or check". Measured against the live account,
   * the largest value is `other` (122 of 330) and nothing in Mercury's set
   * names ACH at all.
   */
  mercuryKind: string | null;
  version: number;
  /** §7 — the operator's classification. Null until the entry is coded. */
  partyId: string | null;
  partyName: string | null;
  lineCount: number;
  debitTotalCents: number;
  creditTotalCents: number;
  balanced: boolean;
  /**
   * §7 — receipts filed against this header.
   *
   * A count rather than a boolean: "there is evidence" and "there are three of
   * them" are different things to a person hunting the missing half of a pair,
   * and a boolean can only say the first. The single-entry read omits it and
   * returns the receipts themselves.
   */
  receiptCount: number;
}

export interface JournalList {
  entries: JournalListRow[];
  /** Matches before paging — the queue count, not the page length. */
  total: number;
  limit: number;
  offset: number;
}

export interface JournalLine {
  id: string;
  lineNumber: number;
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  accountRole: string;
  debitCents: number;
  creditCents: number;
  memo: string | null;
  /**
   * Migration 0079: the line's fund and its code, null in a business without
   * funds; `interfund` marks a line the save wrote itself between funds. Absent
   * only on the tests' hand-made lines.
   */
  fundId?: string | null;
  fundCode?: string | null;
  interfund?: boolean;
  /** Migration 0081: the line's project and its name, and whether its account carries one. */
  projectId?: string | null;
  projectName?: string | null;
  carriesProject?: boolean;
  /** Migration 0091: the line's phase and its name, and whether the line may carry one (a project's lines, never stock). */
  phaseId?: string | null;
  phaseName?: string | null;
  carriesPhase?: boolean;
  /** Migration 0086: the item a stock line moves or a revenue line sells, and how many of its unit. */
  itemId?: string | null;
  itemName?: string | null;
  quantity?: string | null;
  /** Migration 0086: on a cost line, the stock line whose cost it is. */
  costsDetailId?: string | null;
  /** Migration 0086: the line's account holds stock. */
  holdsStock?: boolean;
  /** Migration 0086: a draw names the line, so it is frozen (round 131's IB-3). */
  drawn?: boolean;
  /** Migration 0088: on a stock debit, the out-line whose goods it is (a transfer's, on its own entry). */
  fromDetailId?: string | null;
  /** Migration 0090: the labels the line carries, of the entry's fiscal year. Absent only on the tests' hand-made lines. */
  labels?: LineLabel[];
}

/**
 * Mirrors GET /journals/:id.
 *
 * `version` is the concurrency token every write endpoint requires. Read it
 * here, echo it back unchanged — do not compute or increment it.
 */
/*
 * `receiptCount` is omitted with `lineCount`, mirroring the server: this shape
 * carries the lines themselves, and a screen showing one entry lists its
 * receipts from GET /journals/:id/attachments. A count beside a list is the
 * same fact twice and the two can disagree.
 */
export interface JournalEntry extends Omit<JournalListRow, 'lineCount' | 'receiptCount'> {
  /**
   * When it is due, or null when nobody has recorded terms (migration 0036).
   * Null is never "due today" -- the editor must be able to tell them apart.
   */
  dueDate: string | null;
  /** 0084 (§B.17.3): what a donor received in return for an event gift; null is nothing. Absent on hand-made fixtures. */
  goodsServicesCents?: number | null;
  mercuryTransactionId: string | null;
  mercuryAccountId: string | null;
  /** An imported row's chart bank account (0067); null on any other row. */
  bankAccountId: string | null;
  /**
   * §9.1 — what Mercury said, which since migration 0026 is a different field
   * from `memo`.
   *
   * `memo` is the operator's and the sync no longer writes over it. This is
   * the bank's own text, refreshed on every run, and the reason the split
   * exists: before it, typing a memo destroyed the only copy of the
   * descriptor.
   *
   * Null on every manual entry. On a mercury entry the server has already
   * applied the pre-0026 fallback, so null here means the bank sent no text at
   * all. It EQUALS `memo` on any entry nobody has retyped, which is why the
   * screens show it only when the two differ.
   */
  bankMemo: string | null;
  postedAt: string | null;
  postedBy: string | null;
  dismissedAt: string | null;
  reversesHeaderId: string | null;
  reversedByHeaderId: string | null;
  relatedHeaderId: string | null;
  relationKind: string | null;
  /** The fiscal year this entry closes into retained earnings (0052), or null. */
  closesFiscalYear: number | null;
  /** Migration 0090: the fiscal year the entry's date falls in, whose labels its lines carry. Absent on hand-made fixtures. */
  fiscalYear?: number;
  lines: JournalLine[];
}

// ------------------------------------------------------------------ the QuickBooks import (N7)

/** One check's finding (the #55 plan's §B.11.1.3): a refusal stops the import, a note does not. */
export interface ImportFinding {
  check: number;
  code: string;
  severity: 'refusal' | 'note';
  message: string;
}

/** Mirrors `ImportPlan` in apps/api/src/services/quickbooks-import.ts: what Import would write. */
export interface ImportPlan {
  asOf: string;
  findings: ImportFinding[];
  ready: boolean;
  accountsToCreate: Array<{ code: string; name: string; type: string; role: string }>;
  stockAccounts: string[];
  programsToCreate: string[];
  parties: { donors: number; customers: number; vendors: number; vendorsFor1099: number; alreadyHere: number };
  itemsToCreate: number;
  fundsToCreate: string[];
  lines: Array<{ accountCode: string; accountName: string; fund: string | null; debitCents: number; creditCents: number; item?: string; quantity?: string; fold?: boolean }>;
  byFund: Array<{ fund: string | null; netAssetsClass: 'without' | 'with' | 'retained'; assetsCents: number; liabilitiesCents: number; netAssetsCents: number }>;
  foldedFromQuickBooksCents: number;
  uncleared: Array<{ bank: string; date: string; amountCents: number; payee: string | null }>;
}

export interface ImportResult {
  headerId: string;
  lineCount: number;
  accountsCreated: number;
  programsCreated: number;
  partiesCreated: number;
  itemsCreated: number;
  fundsCreated: number;
}

// ------------------------------------------------------------------ labels (0090)

/** The palette a label's colour comes from (0090's CHECK). */
export const LABEL_COLOURS = ['grey', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple'] as const;
export type LabelColour = (typeof LABEL_COLOURS)[number];

/** A label as a line carries it. */
export interface LineLabel {
  id: string;
  name: string;
  colour: LabelColour | string;
}

/** Mirrors GET /labels?year= (0090, §D.5.2). */
export interface Label {
  id: string;
  fiscalYear: number;
  name: string;
  group: string | null;
  colour: LabelColour;
  archivedAt: string | null;
  rolledFromLabelId: string | null;
  rolledIntoLabelId: string | null;
  lineCount: number;
}

export interface LabelYear {
  fiscalYear: number;
  /** The year is closed (0052): its labels do not change. */
  closed: boolean;
  labels: Label[];
}

/** Mirrors GET /reports/labels: one label, or one family, with ONE total (§D.1). */
export interface LabelReport {
  label: { id: string; name: string; fiscalYear: number };
  family: boolean;
  years: number[];
  from: string | null;
  to: string | null;
  lines: Array<{
    lineId: string;
    headerId: string;
    entryDate: string;
    description: string | null;
    accountCode: string;
    accountName: string;
    fundCode: string | null;
    projectName: string | null;
    labelName: string;
    labelYear: number;
    amountCents: number;
  }>;
  byAccount: Array<{ accountCode: string; accountName: string; amountCents: number }>;
  totalCents: number;
}

// ------------------------------------------------------------------ sync

/** Mirrors GET /sync/status. Field meanings are documented at the route. */
/**
 * One bank's feed.
 *
 * SPLIT OUT OF `SyncStatus` BY 0046, and the split is the point. The status was
 * a single object -- one `lastRunStatus`, one `stale`, one token countdown --
 * and aggregating two banks into that shape recreates precisely the failure
 * `resolveSyncTenant` refused to allow: "a scheduler that quietly synced
 * whichever row sorted first would be a silent half-outage", arriving through
 * the status screen instead.
 *
 * The token countdown is the sharpest case. It is PER TOKEN, and Mercury
 * deletes one unused for 45 days with manual recovery afterwards, so one
 * healthy bank would mask another's credential dying.
 */
export interface BankSyncStatus {
  connectionId: string;
  /** What the operator calls this bank. */
  bank: string;
  provider: string;
  /** Per connection: one business may test one bank while another is live. */
  environment: 'production' | 'sandbox' | string;
  disabled: boolean;
  connectedAt: string;
  /** Mercury accounts mapped to the chart. None means nothing is imported (issue #54). */
  mappedAccounts: number;
  lastRunAt: string | null;
  lastRunStatus: 'succeeded' | 'failed' | 'running' | null;
  /** A failed run's reason. */
  lastRunError: string | null;
  /** A succeeded run's note: it imported, and something beside it did not happen (0077, review PB-3). */
  lastRunNote: string | null;
  lastRunTrigger: string | null;
  lastSuccessAt: string | null;
  hoursSinceSuccess: number | null;
  /** This bank's data is old. */
  stale: boolean;
  /** This bank's credential is approaching deletion — a worse problem. */
  tokenAtRisk: boolean;
  tokenUrgent: boolean;
  daysUntilTokenDeleted: number | null;
  /** Which instant the 45-day countdown is measured from. */
  tokenCountdownFrom: 'last_success' | 'first_attempt' | null;
}

export interface SyncStatus {
  /** Configured intent. */
  scheduleEnabled: boolean;
  /** Whether a cron job actually exists. Display this one. */
  scheduleRegistered: boolean;
  /** When the schedule runs, in words: "daily at 06:00 UTC". */
  scheduleDescription: string;
  /** Across every bank: an exception belongs to the books, not to a feed. */
  openExceptions: number;
  /** Empty when no bank is connected, which is a state the screen must say. */
  banks: BankSyncStatus[];
}

/** One bank's answer to Sync Now: what it did, or why it did not. */
export interface ManualSyncResult {
  connectionId: string;
  bank: string;
  ran: boolean;
  fetched?: number;
  created?: number;
  updated?: number;
  exceptions?: number;
  reason?: string;
  /** Mercury's own words when it refused, e.g. a 403 from an address it does not allow. */
  error?: string;
}


// ------------------------------------------------------------- clearings

export interface ReceivablesQuery {
  /** Substring of the counterparty, as the bank or the operator wrote it. */
  counterparty?: string;
  limit?: number;
  offset?: number;
}

/** Age since the entry date. NOT a statement about when payment was due. */
export type AgingBucket = 'future' | '0-30' | '31-60' | '61-90' | '90+';

/** §6.8 — where an invoice stands. `partly_paid` is posted with some of it matched. */
export type InvoiceStatus = 'draft' | 'open' | 'partly_paid' | 'paid';

/** What the Invoices list is filtered to. `open` includes partly paid. */
export type InvoiceFilter = 'all' | 'draft' | 'open' | 'paid';

/** Mirrors GET /invoices. */
export interface InvoiceRow {
  detailId: string;
  headerId: string;
  entryDate: string;
  invoiceReference: string | null;
  /**
   * When it is due, or null when nobody has recorded terms (migration 0036).
   * The screen offers to fill it in from the invoice PDF.
   */
  dueDate: string | null;
  /** The party's name, falling back to the counterparty text. */
  customer: string | null;
  memo: string | null;
  status: InvoiceStatus;
  amountCents: number;
  paidCents: number;
  outstandingCents: number;
  /** Only when paid: the latest cash date of the payments that settled it. */
  paidDate: string | null;
  /** Only while money is owed. */
  ageDays: number | null;
  bucket: AgingBucket | null;
}

export interface InvoicesResult {
  invoices: InvoiceRow[];
  total: number;
  /** Every status across the search, ignoring the status filter. */
  counts: Record<InvoiceStatus, number>;
  asOf: string;
  limit: number;
  offset: number;
}

export interface InvoicesQuery {
  status?: InvoiceFilter;
  search?: string;
  limit?: number;
  offset?: number;
  /** 0084: leave pledges out, as the Invoices screen does; the forecast and To Do keep them. */
  invoicesOnly?: boolean;
}

/** Mirrors GET /invoices/open. */
export interface OpenInvoice {
  /** The A/R line. This is what POST /clearings takes as invoiceDetailId. */
  detailId: string;
  headerId: string;
  /** The receivable account. POST /writeoffs reads it from this line itself (review issue #30). */
  accountId: string;
  accountCode: string;
  entryDate: string;
  counterparty: string | null;
  invoiceReference: string | null;
  memo: string | null;
  originalCents: number;
  clearedCents: number;
  outstandingCents: number;
  /**
   * When it is due, or null when nobody has recorded terms (migration 0036).
   * Null is never "due today": a screen must be able to tell the two apart.
   */
  dueDate: string | null;
  /** Days past due; negative is not due yet; null when `dueDate` is null. */
  daysOverdue: number | null;
  ageDays: number;
  bucket: AgingBucket;
}

/**
 * §6.7 — a bill with money still owed. Mirrors OpenInvoice, and the sides are
 * the mirror image: a bill CREDITS payables where an invoice debits
 * receivables. See services/payables.ts.
 */
export interface OpenBill {
  detailId: string;
  headerId: string;
  accountId: string;
  accountCode: string;
  entryDate: string;
  counterparty: string | null;
  invoiceReference: string | null;
  memo: string | null;
  originalCents: number;
  clearedCents: number;
  outstandingCents: number;
  ageDays: number;
  bucket: AgingBucket;
  dueDate: string | null;
  daysOverdue: number | null;
}

export interface OpenBillsResult {
  bills: OpenBill[];
  total: number;
  totalOutstandingCents: number;
  byBucket: Record<AgingBucket, number>;
  asOf: string;
  limit: number;
  offset: number;
}

export interface OpenInvoicesResult {
  invoices: OpenInvoice[];
  total: number;
  totalOutstandingCents: number;
  byBucket: Record<AgingBucket, number>;
  /** The TENANT's today, as used for aging (§5.4). Not the browser's. */
  asOf: string;
  limit: number;
  offset: number;
}

/** Mirrors GET /payments/unapplied. */
export interface UnappliedPayment {
  /** The A/R line. This is what POST /clearings takes as paymentDetailId. */
  detailId: string;
  /** POST /writeoffs takes this as paymentHeaderId. */
  headerId: string;
  accountId: string;
  accountCode: string;
  entryDate: string;
  counterparty: string | null;
  memo: string | null;
  originalCents: number;
  appliedCents: number;
  /**
   * Still to apply. Read THIS on a payment, never `open_cents`'s equivalent --
   * migration 0016's header is emphatic that every row carries all four
   * numbers and two are meaningless for any given line.
   */
  unappliedCents: number;
}

export interface UnappliedPaymentsResult {
  payments: UnappliedPayment[];
  total: number;
  totalUnappliedCents: number;
  limit: number;
  offset: number;
}

/** One live match, from GET /payments/:detailId/clearings. */
export interface PaymentClearing {
  /** What voidClearing takes. */
  id: string;
  amountCents: number;
  createdAt: string;
  invoiceDetailId: string;
  invoiceHeaderId: string;
  invoiceEntryDate: string;
  invoiceReference: string | null;
  counterparty: string | null;
}

export interface AllocatePaymentBody {
  paymentDetailId: string;
  allocations: Array<{ invoiceDetailId: string; amountCents: number }>;
}

export interface AllocationResult {
  clearingIds: string[];
  totalAllocatedCents: number;
  paymentRemainingCents: number;
}

/**
 * §6.5 — every field is the operator's decision, and none is defaulted here.
 *
 * `expenseAccountId` in particular: Bad Debt Expense is the usual answer and
 * the API will accept any expense account, so the screen asks rather than
 * assuming. `entryDate` is not the browser's today — see the screen for why
 * the client cannot know the tenant's.
 */
export interface WriteOffBody {
  paymentHeaderId: string;
  invoiceDetailId: string;
  expenseAccountId: string;
  amountCents: number;
  entryDate: string;
  memo?: string;
  /** 0081: the program the expense line goes in, where the invoice's lines name none. */
  projectId?: string;
}

export interface WriteOffResult {
  writeOffHeaderId: string;
  clearingId: string;
  amountCents: number;
}


// --------------------------------------------------------- party aliases

/** Mirrors GET /parties/resolve. */
export interface ResolvedParty {
  partyId: string;
  partyName: string;
  /** 'name' when the counterparty IS the party's name; 'alias' otherwise. */
  matchedOn: 'name' | 'alias';
  /** The alias that matched, so the screen can say why. Null for a name. */
  matchedAlias: string | null;
}

export interface PartyAlias {
  id: string;
  partyId: string;
  alias: string;
  createdAt: string;
  /** Headers whose counterparty matches this alias. */
  matchingHeaders: number;
}

export interface CounterpartySummary {
  counterparty: string;
  /** Every header carrying this name, posted included. */
  headers: number;
  /**
   * Headers an alias would still fill: no party yet, and not inside the closed
   * period.
   *
   * Posted entries in an open period count, because since migration 0027 they
   * can take a party. A counterparty with nothing left to fill is still worth
   * an alias for the entries that have not arrived yet, and the screen has to
   * be able to say which case a row is.
   */
  fillableHeaders: number;
  /** Null when the name maps to nothing — those are the ones needing an alias. */
  partyId: string | null;
  partyName: string | null;
  matchedOn: 'name' | 'alias' | null;
}

/** One month on the Reconcile screen's table. Mirrors GET /reconciliations/months. */
export interface StatementMonth {
  /** Last calendar day of the month: 2026-08-31. */
  monthEnd: string;
  statement: {
    id: string;
    filename: string;
    /** Mercury's own id, or null when somebody uploaded the file themselves. */
    mercuryStatementId: string | null;
    sizeBytes: number;
  } | null;
  reconciliation: {
    id: string;
    statementBalanceCents: number;
    bookBalanceCents: number;
    createdAt: string;
  } | null;
  /**
   * The closing balance the bank stated, from whichever source knows it, and
   * WHICH source. A reconciliation's figure is what the operator confirmed; a
   * statement's is what Mercury reported when the PDF was fetched.
   */
  statementBalanceCents: number | null;
  statementBalanceSource: 'reconciliation' | 'statement' | null;
  /**
   * Mercury transactions dated in this month that nobody has categorized.
   *
   * In the bank and not in the books, so this is usually what a variance is
   * made of. The month's OWN count, not cumulative -- the variance beside it is
   * cumulative, so a month can read 0 here and still be out because of an
   * earlier one.
   */
  uncategorizedCount: number;
  /** Posted movement on this account within the month. */
  bookMovementCents: number;
  /** Cumulative through the month end, which is what a reconciliation checks. */
  bookBalanceCents: number;
}

/** What a Mercury statement fetch did. Mirrors POST /reconciliations/statements/fetch. */
export interface MercuryFetchResult {
  fetched: number;
  alreadyHeld: number;
  /**
   * Statements already held whose closing balance was recorded for the first
   * time. Not a download, so not counted as `fetched`.
   */
  balancesRecorded: number;
  /** Accounts that could not be asked, with the reason. Never silent. */
  skipped: { accountId: string; accountCode: string; reason: string }[];
}

// ---------------------------------------------------------------- the To Do list

/** Who the work is owed to. Local has no source yet; the screen draws its filters from the rows. */
export type TodoCategory = 'federal' | 'state' | 'books';

/** What kind of work, within a category: the finer filters. */
export type TodoGroup =
  | 'returns'
  | 'deposits'
  | 'uncategorized'
  | 'drafts'
  | 'payments'
  | 'payroll'
  | 'bills'
  | 'invoices'
  | 'commitments'
  | 'reconciliation'
  | 'sync'
  | 'setup'
  /** 0089 (§B.19): a time-restricted fund past its date that still holds something. */
  | 'releases';

/**
 * overdue: a deadline of the business's own has passed. due: a deadline ahead.
 * now: waiting on the operator, with no deadline. done: a return recorded filed.
 */
export type TodoStatus = 'overdue' | 'due' | 'now' | 'done';

export type ReturnForm =
  | 'form_941'
  | 'texas_wage_report'
  | 'form_940'
  | 'form_w2'
  // A non-profit's annual return (0078).
  | 'form_990_n'
  | 'form_990_ez'
  | 'form_990'
  | 'form_990_pf'
  // A for-profit's income-tax return (0094).
  | 'schedule_c'
  | 'form_1120s'
  | 'form_1120'
  | 'form_1065'
  // A C corporation's estimated tax installment, marked paid (0095).
  | 'form_1120_estimate';

/** Payroll's returns, recorded at payroll's route; mirrors `PAYROLL_RETURN_FORMS` in `apps/api/src/todo/list.ts`. */
const PAYROLL_RETURN_FORMS: ReadonlySet<ReturnForm> = new Set<ReturnForm>(['form_941', 'texas_wage_report', 'form_940', 'form_w2']);

/**
 * Where a return is recorded as filed: payroll's at payroll's route, and every
 * other, a business's annual return, at the To Do list's (0078, 0094). Payroll's
 * are named rather than the others, so a return added later lands at the annual
 * route: until 2026-10-21 the test was "starts with form_990", and T1's Form
 * 1120-S row would have been sent to payroll's route and refused there.
 */
function filingsPath(form: ReturnForm): string {
  return PAYROLL_RETURN_FORMS.has(form) ? '/payroll/filings' : '/returns/filings';
}

export interface TodoItem {
  /** Stable while the books do not change. */
  key: string;
  category: TodoCategory;
  group: TodoGroup;
  /** A label, Title Case, from the server. */
  title: string;
  /** The date it is due by or concerns; null when it is simply waiting. */
  when: string | null;
  /** The statutory date, when a weekend or holiday moved `when`. */
  movedFrom: string | null;
  status: TodoStatus;
  /** A sentence. */
  what: string;
  /** The screen that does it, or null when no screen can. */
  link: string | null;
  /**
   * On a return's row, which return, so Mark Filed can record it, and the last
   * day of its period: a return is filed after it, so Mark Filed is offered from
   * the day after.
   */
  filing: { form: ReturnForm; year: number; quarter: 1 | 2 | 3 | 4 | null; periodEnd: string } | null;
  /** Once recorded as filed. */
  filed: { id: string; on: string; note: string | null } | null;
  /** On a release's row (0089, §B.19): the fund, its date and what it still holds. Null on every other row. */
  release: { fundId: string; releaseOn: string; amountCents: number } | null;
  /** Whether the Home tab counts it: overdue, now, or due within seven days. The server decides. */
  counted: boolean;
}

export interface TodoList {
  /** The business's today. */
  today: string;
  /** Rows due after this are left off. */
  through: string;
  /** Overdue, then now, then due, then done. */
  items: TodoItem[];
  /** The Home tab's count: overdue and waiting rows, and rows due within seven days. */
  attention: number;
  /** Any at all turns the count red. */
  overdue: number;
  /** What the forecast cannot see, as numbers, for the sentence above Home's figures. */
  queues: { uncategorized: number; unposted: number; unmatched: number };
}

export interface ReturnFiling {
  id: string;
  form: ReturnForm;
  year: number;
  quarter: 1 | 2 | 3 | 4 | null;
  /** The To Do key of the return it records. */
  key: string;
  filedOn: string;
  note: string | null;
  recordedAt: string;
  recordedByEmail: string | null;
  withdrawnAt: string | null;
}

/** Mirrors GET /reports/shareholder (apps/api/src/services/shareholder.ts). */
export interface ShareholderAccount {
  accountId: string;
  code: string;
  name: string;
}

export interface ShareholderQuarter {
  quarter: 1 | 2 | 3 | 4;
  wagesCents: number;
  healthInsuranceCents: number;
  distributionsCents: number;
}

/** One year of Form 7203, Part I. The line numbers are the form's. */
export interface BasisYear {
  year: number;
  beginningCents: number;
  contributionsCents: number;
  ordinaryIncomeCents: number;
  beforeDistributionsCents: number;
  distributionsCents: number;
  /** The part of the distributions that basis could not cover: generally a capital gain. */
  distributionsOverBasisCents: number;
  afterDistributionsCents: number;
  nondeductibleCents: number;
  beforeLossesCents: number;
  ordinaryLossCents: number;
  charitableCents: number;
  carriedInLossesCents: number;
  allowedLossesCents: number;
  suspendedLossesCents: number;
  endingCents: number;
}

export interface ShareholderReport {
  year: number;
  accounts: {
    wages: ShareholderAccount[];
    healthInsurance: ShareholderAccount[];
    distributions: ShareholderAccount;
    paidInCapital: ShareholderAccount;
    charitable: ShareholderAccount | null;
  };
  quarters: ShareholderQuarter[];
  totals: { wagesCents: number; healthInsuranceCents: number; distributionsCents: number };
  firstWageDate: string | null;
  /** The one warning: distributions dated before the year's first officer wage. */
  distributionsBeforeWages: { entries: number; cents: number } | null;
  notPosted: { entries: number; wagesCents: number; distributionsCents: number; contributionsCents: number };
  /** Null when a year since the books began cannot be converted to cash basis; `basisUnavailable` says why. */
  basis: BasisYear | null;
  basisHistory: BasisYear[];
  basisUnavailable: { year: number; code: string; message: string } | null;
  /** Bank rows in the year still uncategorized: in no figure. */
  uncategorized: number;
  firstYear: number;
  /** Newest first. */
  years: number[];
}

/**
 * Migration 0084 (§B.17.2): a gift is money or property received, on the day
 * it arrived. Shaped from apps/api/src/services/donors.ts.
 */
export type GiftKind = 'cash' | 'in_kind' | 'event';

export interface Gift {
  headerId: string;
  date: string;
  kind: GiftKind;
  /** A pledge paid, which is cash. */
  pledgePayment: boolean;
  /** A gift returned through the bank (DO-4): its amount and deductible part are negative. */
  refund: boolean;
  /** The books' amount; an in-kind gift's is never printed on a letter. */
  amountCents: number;
  description: string | null;
  goodsServicesCents: number | null;
  deductibleCents: number;
}

export interface KindTotal {
  count: number;
  totalCents: number;
}

export interface GivingRow {
  /** Null on the row of contributions whose entries name nobody (DO-2). */
  partyId: string | null;
  name: string;
  /** A row not marked a donor is still listed, and says so. */
  isDonor: boolean;
  cash: KindTotal;
  inKind: KindTotal;
  event: KindTotal;
  goodsServicesCents: number;
  pledgesMadeCents: number;
  pledgeChangesCents: number;
  pledgesOpenCents: number;
  /** A single gift whose deductible part is $250 or more. */
  needsAcknowledgment: boolean;
  /** An event payment over $75 with goods or services given in return. */
  needsDisclosure: boolean;
  /**
   * 0085 (PF-3): a private foundation's donor whose contributions in the year,
   * less what they received in return, reach Schedule B's $5,000. NOT the
   * substantial-contributor test. Null elsewhere; absent on older fixtures.
   */
  scheduleB?: boolean | null;
}

export interface GivingReport {
  year: number;
  /** Schedule B's threshold for a private foundation; null for any other business. */
  scheduleBThresholdCents?: number | null;
  donors: GivingRow[];
}

/** GET /foundation/markings (0085, §B.18.5): one account's shares for one fiscal year, in basis points. */
export interface PfMarking {
  accountId: string;
  netInvestmentBp: number | null;
  adjustedNetBp: number | null;
  charitableBp: number | null;
  directConductBp: number | null;
  exemptUse: boolean;
}

export interface PfMarkings {
  year: number;
  from: string;
  to: string;
  accounts: Array<{
    accountId: string;
    code: string;
    name: string;
    type: 'revenue' | 'expense' | 'asset';
    isActive: boolean;
    marking: PfMarking | null;
  }>;
  previousYearMarkings: number;
}

/** GET /reports/990-pf (0085, §B.18.4): a prompt for the CPA, never a ruling. */
/** 0092 (§B.20): the annual returns whose lines the report reads. */
export type ReturnLineForm = '990_pf' | '990' | '990_ez';
export type ReturnColumn = 'a' | 'b' | 'c' | 'd' | 'start' | 'end' | 'amount' | 'total' | 'program' | 'management' | 'fundraising' | 'membership';
export type ReturnColumns = Partial<Record<ReturnColumn, number | null>>;
export type ReturnLineKind = 'revenue' | 'expense' | 'less' | 'asset' | 'liability' | 'net_assets' | 'other_changes' | 'memo' | 'total' | 'blank';

export interface ReturnLineAccount {
  accountId: string;
  code: string;
  name: string;
  values: ReturnColumns;
}

export interface ReturnReportLine {
  part: string;
  line: string;
  label: string;
  kind: ReturnLineKind;
  values: ReturnColumns;
  accounts: ReturnLineAccount[];
  /** A described line's rows: one account each, then the rest. */
  rows?: Array<{ line: string; label: string; values: ReturnColumns }>;
  note?: string;
}

export interface ReturnLinesReport {
  form: ReturnLineForm;
  forms: ReturnLineForm[];
  year: number;
  from: string;
  to: string;
  catalogueYear: number;
  catalogueSentence: string | null;
  parts: Array<{ part: string; title: string; columns: ReturnColumn[] }>;
  lines: ReturnReportLine[];
  notOnAnyLine: Array<ReturnLineAccount & { type: string; reason: 'no_line' | 'not_on_this_form'; mapped: string | null }>;
  needsConfirming: Array<
    ReturnLineAccount & { type: string; part: string; line: string; confirmedYear: number; confirmedLabel: string | null; currentLabel: string }
  >;
  otherChanges: {
    entries: Array<{ headerId: string; date: string; memo: string | null; withoutCents: number; withCents: number; totalCents: number }>;
    withoutCents: number;
    withCents: number;
    totalCents: number;
  };
  netAssets: {
    start: { withoutCents: number; withCents: number; totalCents: number };
    result: { withoutCents: number; withCents: number; totalCents: number };
    end: { withoutCents: number; withCents: number; totalCents: number };
    reconciles: boolean;
  };
  ties: Array<{ label: string; formCents: number; unplacedCents: number; booksCents: number; ties: boolean }>;
  officers: { calendarYear: number; list: Array<{ employeeId: string; name: string; box1Cents: number; box5Cents: number }> } | null;
  cashBasisRefusal: string | null;
}

export interface ReturnLineMapping {
  part: string;
  line: string;
  side: 'revenue' | 'expense' | 'less' | 'asset' | 'liability';
  confirmedYear: number;
}

export interface ReturnLineMap {
  form: ReturnLineForm;
  forms: ReturnLineForm[];
  catalogueYear: number;
  parts: Array<{ part: string; title: string }>;
  lines: Array<{ part: string; line: string; label: string; kind: 'revenue' | 'expense' | 'less' | 'asset' | 'liability' }>;
  accounts: Array<{
    accountId: string;
    code: string;
    name: string;
    type: 'asset' | 'liability' | 'revenue' | 'expense';
    isActive: boolean;
    mapping: ReturnLineMapping | null;
    suggested: string | null;
  }>;
}

export interface Pf990Worksheet {
  year: number;
  from: string;
  to: string;
  operating: boolean;
  rows: Array<{
    accountId: string;
    code: string;
    name: string;
    type: 'revenue' | 'expense';
    perBooksCents: number;
    netInvestmentCents: number;
    adjustedNetCents: number;
    /** Null on revenue, and everywhere when the cash-basis conversion refused. */
    charitableCents: number | null;
    marked: boolean;
  }>;
  totals: {
    revenueCents: number;
    expenseCents: number;
    excessCents: number;
    netInvestmentIncomeCents: number;
    adjustedNetIncomeCents: number;
    charitableCents: number | null;
  };
  cashBasisRefusal: string | null;
  qualifyingDistributions: {
    charitableCents: number | null;
    assetPurchasesCents: number;
    totalCents: number | null;
    purchases: Array<{ headerId: string; date: string; accountCode: string; accountName: string; memo: string | null; amountCents: number }>;
  };
  recoveries: Array<{ headerId: string; date: string; accountCode: string; accountName: string; memo: string | null; bookCreditCents: number; proceedsCents: number }>;
  directConduct: {
    expenseCents: number | null;
    assetPurchasesCents: number;
    totalCents: number | null;
    adjustedNetIncomeCents: number;
    eightyFivePercentCents: number;
  } | null;
  excise: { rateBp: number; netInvestmentIncomeCents: number; estimateCents: number };
  disqualifiedPayments: Array<{
    headerId: string;
    date: string;
    partyId: string;
    partyName: string;
    relationship: DisqualifiedRelationship;
    paidCents: number;
    memo: string | null;
  }>;
  unmarked: Array<{ accountId: string; code: string; name: string }>;
}

export interface DonorLetter {
  year: number;
  business: { legalName: string; ein: string; address: string[]; is501c3: boolean };
  donor: { partyId: string; name: string; address: string[] };
  gifts: Gift[];
  totalReceivedCents: number;
  totalDeductibleCents: number;
  anyGoodsServices: boolean;
}
